import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Lottie from "lottie-react";
import {
  AlertCircle,
  Calendar,
  Car,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  FileText,
  Info,
  Loader2,
  User,
  Utensils,
  X
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BusGif from "../assets/Bus.gif";
import CapitalNAnimation from "../assets/Capital N.json";
import CarGif from "../assets/Car.gif";
import { useConfig } from "../context/ConfigContext";
import axios from "../utils/axiosHandler";

/// Initialize stripe promise - this will be set after we fetch the key
let stripePromise = null;

const getStripePromise = async () => {
  if (!stripePromise) {
    try {
      const response = await axios.get("/api/config/stripe");
      if (response.data.publicKey) {
        stripePromise = loadStripe(response.data.publicKey);
      } else {
        console.error("No Stripe public key found in config");
        // Fallback to environment variable if available
        if (process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
          stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
        }
      }
    } catch (error) {
      console.error("Error fetching Stripe config:", error);
      // Fallback to environment variable if API fails
      if (process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
        stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
      }
    }
  }
  return stripePromise;
};

const CustomDatePicker = ({
  selectedDate,
  onDateChange,
  minDate,
  maxDate,
  availableDays = [],
  config,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState(null);

  // Get the current date for highlighting today
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Parse dates for comparison
  const minDateTime = minDate ? new Date(minDate) : null;
  const maxDateTime = maxDate ? new Date(maxDate) : null;

  // Get available dates for the current month (only show dates that are available)
  const getAvailableDatesInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const availableDates = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      if (isDateAvailable(currentDate)) {
        availableDates.push(currentDate);
      }
    }

    return availableDates;
  };

  // Check if date is available
  const isDateAvailable = (date) => {
    if (!date) return false;

    // Check min/max bounds
    if (minDateTime && date < minDateTime) return false;
    if (maxDateTime && date > maxDateTime) return false;

    // Check available days if specified
    if (availableDays.length > 0) {
      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const dayName = dayNames[date.getDay()];
      if (!availableDays.includes(dayName)) return false;
    }

    return true;
  };

  // Check if date is selected
  const isDateSelected = (date) => {
    if (!date || !selectedDate) return false;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    return dateString === selectedDate;
  };

  // Check if date is today
  const isToday = (date) => {
    if (!date) return false;
    return date.toDateString() === today.toDateString();
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    // Fix timezone issue by creating the date string in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    onDateChange(dateString);
  };

  // Navigate months
  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  // Check if we can navigate to next/previous month
  const canNavigatePrev = () => {
    if (!minDateTime) return true;
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(currentMonth.getMonth() - 1);
    return (
      prevMonth.getFullYear() > minDateTime.getFullYear() ||
      (prevMonth.getFullYear() === minDateTime.getFullYear() &&
        prevMonth.getMonth() >= minDateTime.getMonth())
    );
  };

  const canNavigateNext = () => {
    if (!maxDateTime) return true;
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(currentMonth.getMonth() + 1);
    return (
      nextMonth.getFullYear() < maxDateTime.getFullYear() ||
      (nextMonth.getFullYear() === maxDateTime.getFullYear() &&
        nextMonth.getMonth() <= maxDateTime.getMonth())
    );
  };

  const availableDates = getAvailableDatesInMonth(currentMonth);
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  return (
    <div className="card-date-picker">
      {/* Header with icon and title */}
      {/* <div className="picker-header">
        <div className="header-icon">
          <Calendar className="w-6 h-6" />
        </div>
        <h3 className="header-title">Choose Your Date</h3>
      </div> */}

      {/* Month Navigation */}
      <div className="month-navigation">
        <button
          type="button"
          onClick={() => navigateMonth(-1)}
          disabled={!canNavigatePrev()}
          className="nav-button"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>

        <h4 className="month-title">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h4>

        <button
          type="button"
          onClick={() => navigateMonth(1)}
          disabled={!canNavigateNext()}
          className="nav-button"
          aria-label="Next month"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      </div>

      {/* Available Dates Grid */}
      <div className="dates-container">
        {availableDates.length === 0 ? (
          <div className="no-dates">
            <Calendar className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm text-center">
              No available dates in {monthNames[currentMonth.getMonth()]}
            </p>
          </div>
        ) : (
          <div className="p-2 dates-grid grid grid-cols-6 xs:grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 xl:grid-cols-8">
            {availableDates.map((date, index) => (
              <div
                key={index}
                className={`date-card ${isDateSelected(date) ? "selected" : ""
                  } ${isToday(date) ? "today" : ""} ${hoveredDate &&
                    date.toDateString() === hoveredDate.toDateString()
                    ? "hovered"
                    : ""
                  }`}
                onClick={() => handleDateSelect(date)}
                onMouseEnter={() => setHoveredDate(date)}
                onMouseLeave={() => setHoveredDate(null)}
              >
                <div className="date-number">{date.getDate()}</div>
                <div className="date-month">{monthNames[date.getMonth()]}</div>
                <div className="date-day">{dayNames[date.getDay()]}</div>
                {isToday(date) && <div className="today-badge">Today</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .card-date-picker {
          width: 100%;
          background: white;
          border-radius: 16px;
          padding: 1.5rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
          border: 1px solid #e5e7eb;
        }

        .picker-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .header-icon {
          width: 40px;
          height: 40px;
          background: ${config?.colors?.primary || "#6366f1"};
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .header-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: ${config?.colors?.textPrimary || "#111827"};
          margin: 0;
        }

        .month-navigation {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
          padding: 0 0.5rem;
        }

        .nav-button {
          width: 48px;
          height: 48px;
          border: 3px solid ${config?.colors?.primary || "#6366f1"};
          background: white;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          color: ${config?.colors?.primary || "#6366f1"};
          box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
          font-weight: bold;
        }

        .nav-button:hover:not(:disabled) {
          background: ${config?.colors?.primary || "#6366f1"};
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(99, 102, 241, 0.4);
        }

        .nav-button:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
        }

        .nav-button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .month-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: ${config?.colors?.textPrimary || "#111827"};
          margin: 0;
          text-align: center;
        }

        .dates-container {
          min-height: 200px;
        }

        .no-dates {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 200px;
        }

        .dates-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 0.75rem;
          max-height: 400px;
          overflow-y: auto;
          padding-right: 4px;
        }

        .dates-grid::-webkit-scrollbar {
          width: 4px;
        }

        .dates-grid::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 2px;
        }

        .dates-grid::-webkit-scrollbar-thumb {
          background: ${config?.colors?.primary || "#6366f1"};
          border-radius: 2px;
        }

        .date-card {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          min-height: 80px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        .date-card:hover,
        .date-card.hovered {
          border-color: ${config?.colors?.primary || "#6366f1"};
          background: linear-gradient(
            135deg,
            ${config?.colors?.primary || "#6366f1"}10 0%,
            ${config?.colors?.accent || "#8b5cf6"}10 100%
          );
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(99, 102, 241, 0.15);
        }

        .date-card.selected {
          background: linear-gradient(
            135deg,
            ${config?.colors?.primary || "#6366f1"} 0%,
            ${config?.colors?.accent || "#8b5cf6"} 100%
          );
          border-color: ${config?.colors?.primary || "#6366f1"};
          color: white;
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 10px 30px rgba(99, 102, 241, 0.3);
        }

        .date-card.today:not(.selected) {
          border-color: ${config?.colors?.secondary || "#f59e0b"};
          background: linear-gradient(
            135deg,
            ${config?.colors?.secondary || "#f59e0b"}15 0%,
            #fbbf2415 100%
          );
        }

        .date-number {
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1;
          color: inherit;
        }

        .date-month {
          font-size: 0.75rem;
          font-weight: 500;
          margin-top: 0.25rem;
          opacity: 0.8;
          color: inherit;
        }

        .date-day {
          font-size: 0.7rem;
          font-weight: 500;
          margin-top: 0.125rem;
          opacity: 0.7;
          color: inherit;
        }

        .today-badge {
          position: absolute;
          top: 4px;
          right: 4px;
          background: ${config?.colors?.secondary || "#f59e0b"};
          color: white;
          font-size: 0.6rem;
          font-weight: 600;
          padding: 0.125rem 0.375rem;
          border-radius: 6px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .date-card.selected .today-badge {
          background: rgba(255, 255, 255, 0.3);
        }

        @media (max-width: 768px) {
          .card-date-picker {
            padding: 1rem;
          }

          .dates-grid {
            grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
            gap: 0.5rem;
          }

          .date-card {
            padding: 0.75rem;
            min-height: 70px;
          }

          .date-number {
            font-size: 1.25rem;
          }

          .date-month {
            font-size: 0.7rem;
          }

          .date-day {
            font-size: 0.65rem;
          }

          .header-title {
            font-size: 1.1rem;
          }

          .month-title {
            font-size: 1rem;
          }
        }

        @media (max-width: 480px) {
          .dates-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .date-card {
            min-height: 60px;
            padding: 0.5rem;
          }

          .picker-header {
            margin-bottom: 1rem;
          }

          .month-navigation {
            margin-bottom: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

const BookingForm = ({ service }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { config } = useConfig();

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [duration, setDuration] = useState(() => {
    if (service?.timeSlots) {
      const defaultSlot = service.timeSlots.find(slot => slot.isDefault);
      return defaultSlot ? defaultSlot.duration : (service.timeSlots[0]?.duration || 60);
    }
    return 60;
  });
  const [loading, setLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [availableDurations, setAvailableDurations] = useState([]);

  // New booking type state
  const [bookingType, setBookingType] = useState(""); // "single" or "package"
  const [selectedClass, setSelectedClass] = useState(""); // Class 5, N, or Class 4
  const [selectedPackage, setSelectedPackage] = useState(null); // Package details
  const [needsCarRental, setNeedsCarRental] = useState(false);
  const [needsRoadTestBooking, setNeedsRoadTestBooking] = useState(false);

  // Mobile modal state
  const [showMobileSummary, setShowMobileSummary] = useState(false);
  const [isSummaryVisible, setIsSummaryVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Multi-booking state
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [showAddAnother, setShowAddAnother] = useState(false);
  const [addAnotherResponse, setAddAnotherResponse] = useState("");

  const [customerInfo, setCustomerInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    location: "",
  });

  const [formResponses, setFormResponses] = useState({});
  const [calculatedPrice, setCalculatedPrice] = useState({
    basePrice: 0,
    optionPrices: 0,
    subtotal: 0,
    gst: 0,
    pst: 0,
    totalAmount: 0,
  });

  // Package/Plans state - fetched from backend
  const [packageOptions, setPackageOptions] = useState({});
  const [plansLoading, setPlansLoading] = useState(false);

  // ICBC Locations state
  const [icbcLocations, setIcbcLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  // Call assistance modal state
  const [showCallAssistanceModal, setShowCallAssistanceModal] = useState(false);

  // Fetch ICBC locations on component mount
  useEffect(() => {
    const fetchIcbcLocations = async () => {
      try {
        const response = await axios.get('/api/icbc/locations');
        if (response.data.success) {
          setIcbcLocations(response.data.locations);
        }
      } catch (error) {
        console.error('Error fetching ICBC locations:', error);
        // Fallback to empty array if fetch fails
        setIcbcLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };

    fetchIcbcLocations();
  }, []);

  // Function to save package to localStorage for cart display
  const addPackageToCart = (pkg) => {
    try {
      const packageData = {
        class: selectedClass,
        name: pkg.name,
        price: pkg.price,
        packageBasePrice: pkg.price, // Store original package price separately
        lessons: pkg.lessons,
        duration: pkg.duration,
        savings: pkg.savings,
        description: pkg.description,
        bookingPageUrl: window.location.pathname, // Store the current booking page URL
        currentStep: currentStep, // Store the current step to restore later
        bookingType: bookingType,
        timestamp: Date.now()
      };

      localStorage.setItem('selectedBookingPackage', JSON.stringify(packageData));

      // Dispatch a custom event to notify the Navbar
      window.dispatchEvent(new Event('bookingPackageUpdated'));
    } catch (error) {
      console.error("Error saving package to localStorage:", error);
    }
  };

  // Dynamic step configuration based on booking type
  const getSteps = () => {
    if (bookingType === "single") {
      return [
        { id: 1, label: "Booking Type", icon: FileText },
        { id: 2, label: "Class Selection", icon: Car },
        { id: 3, label: "Date & Time", icon: Calendar },
        { id: 4, label: "Information", icon: User },
        { id: 5, label: "Extras", icon: FileText },
        { id: 6, label: "Payment", icon: CreditCard },
      ];
    } else if (bookingType === "package") {
      // Skip Extras step for Road Test Package
      const isRoadTestPackage = selectedPackage?.name === "Road Test Package";

      if (isRoadTestPackage) {
        return [
          { id: 1, label: "Booking Type", icon: FileText },
          { id: 2, label: "Class Selection", icon: Car },
          { id: 3, label: "Package", icon: FileText },
          { id: 4, label: "Schedule Lessons", icon: Calendar },
          { id: 5, label: "Information", icon: User },
          { id: 6, label: "Payment", icon: CreditCard },
        ];
      } else {
        return [
          { id: 1, label: "Booking Type", icon: FileText },
          { id: 2, label: "Class Selection", icon: Car },
          { id: 3, label: "Package", icon: FileText },
          { id: 4, label: "Schedule Lessons", icon: Calendar },
          { id: 5, label: "Information", icon: User },
          { id: 6, label: "Extras", icon: FileText },
          { id: 7, label: "Payment", icon: CreditCard },
        ];
      }
    } else {
      return [
        { id: 1, label: "Booking Type", icon: FileText },
      ];
    }
  };

  // Make steps reactive to selectedPackage changes
  const steps = getSteps();

  // Calculate progress percentage
  const getProgressPercentage = () => {
    return ((currentStep - 1) / (steps.length - 1)) * 100;
  };

  // Helper function to get config colors with fallbacks
  const getConfigColor = (colorKey, fallback) => {
    return config?.colors?.[colorKey] || fallback;
  };

  // Helper function to get config color with opacity
  const getConfigColorWithOpacity = (colorKey, opacity, fallback) => {
    const color = getConfigColor(colorKey, fallback);
    return `${color}${opacity}`;
  };

  // Check if step is complete
  const isStepComplete = (stepId) => {
    if (bookingType === "single") {
      switch (stepId) {
        case 1: // Booking Type
          return bookingType !== "";
        case 2: // Class Selection
          return selectedClass !== "";
        case 3: // Date & Time
          return selectedBookings.length > 0;
        case 4: // Customer Information
          return (
            customerInfo.firstName && customerInfo.lastName && customerInfo.email && customerInfo.phone && customerInfo.location
          );
        case 5: // Extras (car rental, road test booking)
          return currentStep >= 5; // Optional step - always allow to proceed
        case 6: // Payment
          return currentStep > 6 || (currentStep === 6 && (!service?.requiresPayment || calculatedPrice.totalAmount === 0));
        default:
          return false;
      }
    } else if (bookingType === "package") {
      const isRoadTestPackage = selectedPackage?.name === "Road Test Package";

      switch (stepId) {
        case 1: // Booking Type
          return bookingType !== "";
        case 2: // Class Selection
          return selectedClass !== "";
        case 3: // Package Selection
          return selectedPackage !== null;
        case 4: // Schedule Lessons - must have booked all lessons in the package
          return selectedPackage && selectedBookings.length === selectedPackage.lessons;
        case 5: // Customer Information
          return (
            customerInfo.firstName && customerInfo.lastName && customerInfo.email && customerInfo.phone && customerInfo.location
          );
        case 6: // Extras (car rental, road test booking) - only for non-Road Test packages
          if (isRoadTestPackage) {
            return false; // This step doesn't exist for Road Test Package
          } else {
            return currentStep >= 6; // Optional step - always allow to proceed
          }
        case 7: // Payment - for all packages including Road Test Package
          return currentStep > 7 || (currentStep === 7 && (!service?.requiresPayment || calculatedPrice.totalAmount === 0));
        default:
          return false;
      }
    } else {
      // No booking type selected yet
      return stepId === 1 ? bookingType !== "" : false;
    }
  };

  // Navigation functions
  const canGoNext = () => {
    return isStepComplete(currentStep);
  };

  const scrollToForm = () => {
    // Scroll to the booking form with smooth animation
    const formElement = document.querySelector('.booking-form');
    if (formElement) {
      // Detect if user is on mobile device
      const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      // Adjust offset based on device type
      const yOffset = isMobile ? -120 : -100; // More offset for mobile to account for smaller screens
      const y = formElement.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };

  const scrollToNextButton = () => {
    // Detect if user is on mobile device
    const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Scroll to the next button with smooth animation
    setTimeout(() => {
      const nextButton = document.querySelector('.submit-btn:not([disabled])');
      if (nextButton) {
        // Adjust offset based on device type and screen size
        let yOffset;
        if (isMobile) {
          // Center the next button in the middle of the mobile screen
          // Calculate offset to position button in center of viewport
          const buttonHeight = 48; // Approximate button height
          const viewportHeight = window.innerHeight;
          yOffset = -(viewportHeight / 2) + (buttonHeight / 2);
        } else {
          yOffset = -50;
        }

        const y = nextButton.getBoundingClientRect().top + window.pageYOffset + yOffset;

        // Use smooth scrolling, but fallback to instant on older browsers
        try {
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        } catch (e) {
          // Fallback for browsers that don't support smooth scrolling
          window.scrollTo(0, y);
        }
      }
    }, isMobile ? 100 : 100); // Same delay for mobile and desktop
  };

  const nextStep = () => {
    if (canGoNext() && currentStep < steps.length) {
      // Show call assistance modal when moving from step 2 (class selection) to step 3 (date & time) for single lesson
      if (currentStep === 2 && bookingType === "single") {
        setShowCallAssistanceModal(true);
        return; // Don't proceed to next step yet
      }

      // Show call assistance modal when moving from step 3 (package selection) to step 4 (date & time) for packages
      if (currentStep === 3 && bookingType === "package") {
        setShowCallAssistanceModal(true);
        return; // Don't proceed to next step yet
      }

      let nextStepNumber = currentStep + 1;

      // Skip extras step for Road Test Package
      if (bookingType === "package" && selectedPackage?.name === "Road Test Package" && currentStep === 5) {
        // Skip from step 5 (Information) directly to step 7 (Payment)
        nextStepNumber = 7;
      }

      setCurrentStep(nextStepNumber);
      setError("");
      // Scroll to form after a short delay to allow state update
      setTimeout(scrollToForm, 100);
    }
  };

  const proceedToNextStepAfterModal = () => {
    setShowCallAssistanceModal(false);

    // Determine next step based on booking type
    let nextStepNumber;
    if (bookingType === "single" && currentStep === 2) {
      nextStepNumber = 3; // Go to step 3 (Date & Time) for single lessons
    } else if (bookingType === "package" && currentStep === 3) {
      nextStepNumber = 4; // Go to step 4 (Schedule Lessons) for packages
    } else {
      nextStepNumber = currentStep + 1; // Default fallback
    }

    setCurrentStep(nextStepNumber);
    setError("");
    setTimeout(scrollToForm, 100);
  };

  const closeCallAssistanceModal = () => {
    setShowCallAssistanceModal(false);
  };
  const prevStep = () => {
    if (currentStep > 1) {
      let prevStepNumber = currentStep - 1;

      // Skip extras step for Road Test Package when going back
      if (bookingType === "package" && selectedPackage?.name === "Road Test Package" && currentStep === 7) {
        // Skip from step 7 (Payment) directly to step 5 (Information)
        prevStepNumber = 5;
      }

      setCurrentStep(prevStepNumber);
      setError("");
      // Scroll to form after a short delay to allow state update
      setTimeout(scrollToForm, 100);
    }
  };

  // Fetch plans from backend API
  useEffect(() => {
    const fetchPlans = async () => {
      if (!service?.slug) return;

      setPlansLoading(true);
      try {
        const response = await axios.get(`/api/plans/service/${service.slug}`);

        if (response.data && response.data.plansByClass) {
          // Transform backend plans to match frontend package structure
          const transformedPlans = {};

          Object.keys(response.data.plansByClass).forEach((classType) => {
            const plans = response.data.plansByClass[classType];

            transformedPlans[classType] = plans.map(plan => ({
              id: plan._id,
              name: plan.name.replace(`${classType} - `, ''), // Remove class prefix from name
              lessons: plan.numberOfSessions,
              price: plan.basePrice,
              duration: plan.durationValue,
              savings: plan.savings || 0,
              description: plan.description,
              restricted: plan.isRestricted,
              hasCarRental: plan.hasCarRental,
              // Include additional plan data for reference
              planId: plan._id,
              planType: plan.planType,
              currency: plan.currency,
              features: plan.features,
              hasGPS: plan.hasGPS,
            }));
          });

          setPackageOptions(transformedPlans);
          console.log('Loaded plans from backend:', transformedPlans);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        // Fallback to empty object - plans won't be available
        setPackageOptions({});
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, [service?.slug]);

  // Restore booking state from localStorage on page load
  useEffect(() => {
    try {
      const packageData = localStorage.getItem('selectedBookingPackage');
      if (packageData) {
        const booking = JSON.parse(packageData);

        // Only restore if it matches this page
        if (booking.bookingPageUrl === window.location.pathname) {
          console.log('Restoring booking from cart:', booking);

          // Restore booking type
          setBookingType(booking.bookingType);

          // Restore class selection
          if (booking.class) {
            setSelectedClass(booking.class);
          }

          // Restore customer information if available
          if (booking.customerInfo) {
            setCustomerInfo(booking.customerInfo);
          }

          // Restore form responses if available
          if (booking.formResponses) {
            setFormResponses(booking.formResponses);
          }

          // Restore extras if available
          if (booking.needsCarRental !== undefined) {
            setNeedsCarRental(booking.needsCarRental);
          }
          if (booking.needsRoadTestBooking !== undefined) {
            setNeedsRoadTestBooking(booking.needsRoadTestBooking);
          }

          // Restore package information if it's a package booking
          if (booking.bookingType === "package" && booking.lessons) {
            // Find the original package price from packageOptions to ensure we always have the correct base price
            let originalPackagePrice = booking.packageBasePrice; // Try to use stored base price first

            // If no packageBasePrice exists (old cart data), look it up from packageOptions
            if (!originalPackagePrice && booking.class && packageOptions[booking.class]) {
              const originalPackage = packageOptions[booking.class].find(
                pkg => pkg.name === booking.name && pkg.lessons === booking.lessons
              );
              if (originalPackage) {
                originalPackagePrice = originalPackage.price;
                console.log('Migrated old cart data, found original package price:', originalPackagePrice);
              }
            }

            // Final fallback to stored price (should never happen)
            if (!originalPackagePrice) {
              originalPackagePrice = booking.price;
            }

            setSelectedPackage({
              name: booking.name,
              lessons: booking.lessons,
              price: originalPackagePrice, // Use original package base price
              duration: booking.duration,
              savings: booking.savings,
              description: booking.description
            });

            // Restore scheduled bookings for the package if available
            if (booking.scheduledBookings && booking.scheduledBookings.length > 0) {
              setSelectedBookings(booking.scheduledBookings);
              console.log('Restored scheduled bookings for package:', booking.scheduledBookings);
            }

            // Restore to the saved step, or default to step 4 if no step saved
            if (currentStep === 1) {
              setCurrentStep(booking.currentStep || 4);
            }
          }

          // For single lessons, restore all bookings
          if (booking.bookingType === "single") {
            // Restore ALL bookings if available
            if (booking.allBookings && booking.allBookings.length > 0) {
              setSelectedBookings(booking.allBookings);
              console.log('Restored all bookings from cart:', booking.allBookings);
            } else if (booking.date && booking.startTime && booking.endTime) {
              // Fallback: restore single booking (old format)
              const restoredBooking = {
                id: Date.now(),
                date: booking.date,
                startTime: booking.startTime,
                endTime: booking.endTime,
                duration: booking.duration,
                pricing: {
                  basePrice: booking.price,
                  optionPrices: 0,
                  subtotal: booking.price,
                  gst: 0,
                  pst: 0,
                  totalAmount: booking.price
                }
              };
              setSelectedBookings([restoredBooking]);
            }

            // Restore to the saved step, or navigate appropriately
            if (currentStep === 1) {
              if (booking.currentStep) {
                // Use the saved step if available
                setCurrentStep(booking.currentStep);
              } else if (booking.date && booking.startTime) {
                // Fallback: Skip to Information step since date/time is already selected
                setCurrentStep(4);
              } else {
                // Fallback: Go to Date & Time if not yet selected
                setCurrentStep(3);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error restoring booking from localStorage:", error);
    }
  }, []); // Run only once on mount

  // Set up available durations based on service type
  useEffect(() => {
    if (service) {
      if (
        service.category === "driving_class" ||
        service.pricing.pricingType === "duration_based"
      ) {
        const serviceDurations = service.timeSlots.map((slot) => slot.duration);
        let uniqueDurations = [...new Set(serviceDurations)].sort(
          (a, b) => a - b
        );

        // Filter out 90-minute option for single bookings (only 60 min allowed)
        if (bookingType === "single") {
          uniqueDurations = uniqueDurations.filter((dur) => dur === 60);
        }

        setAvailableDurations(uniqueDurations);

        // Find default time slot duration, fallback to first available or 60
        const defaultSlot = service.timeSlots.find(slot => slot.isDefault && (bookingType !== "single" || slot.duration === 60));
        const defaultDuration = defaultSlot ? defaultSlot.duration : (uniqueDurations[0] || 60);
        setDuration(defaultDuration);
      } else {
        // For non-driving classes, find default slot or use first
        const defaultSlot = service.timeSlots.find(slot => slot.isDefault);
        const defaultDuration = defaultSlot ? defaultSlot.duration : (service.timeSlots[0]?.duration || 60);
        setAvailableDurations([defaultDuration]);
        setDuration(defaultDuration);
      }
    }
  }, [service, bookingType]);

  // Function to scroll to time slots section
  const scrollToTimeSlots = () => {
    const timeSlotSection = document.getElementById('time-slots-section');
    if (timeSlotSection) {
      const headerOffset = 100; // Offset for any fixed headers
      const elementPosition = timeSlotSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Add highlight animation after scroll completes
      setTimeout(() => {
        timeSlotSection.classList.add('highlight');
        // Remove highlight class after animation completes
        setTimeout(() => {
          timeSlotSection.classList.remove('highlight');
        }, 2000);
      }, 500);
    }
  };

  // Load available time slots when date or duration changes
  useEffect(() => {
    if (selectedDate && service?.slug) {
      loadAvailableSlots();
      // Scroll to time slots section after a short delay to ensure the section is rendered
      setTimeout(() => {
        scrollToTimeSlots();
      }, 300);
    }
  }, [selectedDate, duration, service?.slug]);

  // Calculate total price for all selected bookings
  useEffect(() => {
    calculateTotalPrice();
  }, [selectedBookings, formResponses, service, selectedPackage, needsCarRental, needsRoadTestBooking]);

  // Sync selectedClass with formResponses for validation
  useEffect(() => {
    if (selectedClass && service) {
      const classField = service.formFields?.find(
        (field) => field.label === "Class" || field.fieldId?.toLowerCase() === "class"
      );
      if (classField && formResponses[classField.fieldId] !== selectedClass) {
        handleFormResponseChange(classField.fieldId, selectedClass);
      }
    }
  }, [selectedClass, service]);

  // Clean up localStorage when booking type changes from package or when package is deselected
  useEffect(() => {
    // Don't clean up if we haven't made a selection yet (initial state)
    if (bookingType === "" && selectedPackage === null) {
      return;
    }

    // Check if there's a booking in localStorage that matches current booking type
    try {
      const packageData = localStorage.getItem('selectedBookingPackage');
      if (packageData) {
        const booking = JSON.parse(packageData);

        // If the booking in cart matches the current booking type, keep it
        if (booking.bookingType === bookingType) {
          return; // Don't clear cart if it matches current booking type
        }
      }
    } catch (error) {
      console.error("Error checking booking package:", error);
    }

    if (bookingType !== "package" || !selectedPackage) {
      // Remove booking package from localStorage if not in package flow or package deselected
      try {
        localStorage.removeItem('selectedBookingPackage');
        window.dispatchEvent(new Event('bookingPackageUpdated'));
      } catch (error) {
        console.error("Error removing package from localStorage:", error);
      }
    }
  }, [bookingType, selectedPackage]);

  // Restore booking state when returning from cart
  useEffect(() => {
    try {
      const packageData = localStorage.getItem('selectedBookingPackage');
      if (packageData) {
        const pkg = JSON.parse(packageData);

        // Only restore if we're on the same booking page
        if (pkg.bookingPageUrl === window.location.pathname) {
          // Restore the booking state
          if (pkg.bookingType) {
            setBookingType(pkg.bookingType);
          }
          if (pkg.class) {
            setSelectedClass(pkg.class);
          }
          if (pkg.currentStep) {
            setCurrentStep(pkg.currentStep);
          }

          // Restore the selected package
          if (pkg.bookingType === 'package') {
            const packageToRestore = {
              name: pkg.name,
              price: pkg.price,
              lessons: pkg.lessons,
              duration: pkg.duration,
              savings: pkg.savings,
              description: pkg.description
            };
            setSelectedPackage(packageToRestore);

            // Auto-set car rental for Road Test Package
            if (pkg.name === "Road Test Package") {
              setNeedsCarRental(true);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error restoring booking state:", error);
    }
  }, []); // Only run on mount

  // Update saved step and customer info only when explicitly needed (not on every render)
  useEffect(() => {
    // Only update cart when we have actual bookings and we're past the initial steps
    if (currentStep < 3) return; // Don't update cart before date/time selection

    // Skip if price is not yet calculated (default state)
    if (calculatedPrice.totalAmount === 0 && (needsCarRental || needsRoadTestBooking || (selectedPackage && selectedPackage.price > 0))) {
      return; // Price hasn't been calculated yet, wait for next render
    }

    try {
      const packageData = localStorage.getItem('selectedBookingPackage');
      if (!packageData) return; // No cart to update

      const booking = JSON.parse(packageData);

      // Only update if we're on the same booking page
      if (booking.bookingPageUrl === window.location.pathname) {
        // Update for both packages and single lessons
        if ((bookingType === 'package' && selectedPackage) ||
          (bookingType === 'single' && selectedBookings.length > 0)) {

          // Create updated booking data
          const updatedBooking = {
            ...booking,
            currentStep: currentStep,
            customerInfo: customerInfo,
            formResponses: formResponses,
            needsCarRental: needsCarRental,
            needsRoadTestBooking: needsRoadTestBooking,
            price: calculatedPrice.totalAmount,
            // NEVER overwrite packageBasePrice once it's set - it should remain the original package price
            // Only set it if it doesn't exist yet (this preserves the value through cart navigation)
            packageBasePrice: booking.packageBasePrice, // Keep existing value, never overwrite
            // Store scheduled bookings for both packages and single lessons
            scheduledBookings: selectedBookings.length > 0 ? selectedBookings : undefined,
            // Update description to reflect current bookings
            description: bookingType === 'single' && selectedBookings.length > 0
              ? selectedBookings.map((b, idx) =>
                `Lesson ${idx + 1}: ${b.duration} min on ${new Date(b.date).toLocaleDateString()} at ${b.startTime}`
              ).join(' | ')
              : booking.description
          };

          // Only update if something actually changed
          const hasChanged = JSON.stringify(updatedBooking) !== JSON.stringify(booking);
          if (hasChanged) {
            localStorage.setItem('selectedBookingPackage', JSON.stringify(updatedBooking));
            window.dispatchEvent(new Event('bookingPackageUpdated'));
            console.log('Updated cart with current state, price:', calculatedPrice.totalAmount);
          }
        }
      }
    } catch (error) {
      console.error("Error updating saved step:", error);
    }
  }, [currentStep, needsCarRental, needsRoadTestBooking, selectedBookings, customerInfo, formResponses, bookingType, selectedPackage, calculatedPrice]); // Update when any relevant state changes

  // Mobile detection and summary visibility detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    const handleScroll = () => {
      if (window.innerWidth <= 768) {
        // On mobile, always show toggle button since summary is hidden
        setIsSummaryVisible(false);
      } else {
        // On desktop, check if summary is visible
        const summaryElement = document.querySelector('.booking-summary');
        if (summaryElement) {
          const rect = summaryElement.getBoundingClientRect();
          const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
          setIsSummaryVisible(isVisible);
        }
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state

    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const getMinDate = () => {
    const now = new Date();
    const minNoticeMs = (service?.minNoticeHours || 2) * 60 * 60 * 1000;
    const minDate = new Date(now.getTime() + minNoticeMs);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDateOnly = new Date(minDate);
    minDateOnly.setHours(0, 0, 0, 0);

    if (minDateOnly <= today) {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const year = tomorrow.getFullYear();
      const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
      const day = String(tomorrow.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    const year = minDate.getFullYear();
    const month = String(minDate.getMonth() + 1).padStart(2, '0');
    const day = String(minDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getMaxDate = () => {
    const now = new Date();
    const maxDays = service?.advanceBookingDays || 30;
    const maxDate = new Date();
    maxDate.setDate(now.getDate() + maxDays);
    const year = maxDate.getFullYear();
    const month = String(maxDate.getMonth() + 1).padStart(2, '0');
    const day = String(maxDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadAvailableSlots = async () => {
    setLoading(true);
    setSelectedTimeSlot(null);
    try {
      const response = await axios.get(
        `/api/booking-services/${service.slug}/availability/${selectedDate}?duration=${duration}`
      );

      console.log('Availability response:', response.data);

      // Always set slots if they exist, regardless of availability
      if (response.data.slots) {
        setAvailableSlots(response.data.slots);

        // Only show error if there are genuinely no slots generated
        if (response.data.slots.length === 0) {
          setError(response.data.reason || "No time slots available for this date");
        } else {
          setError("");
        }

        // Clear selected time slot if it's no longer available
        if (selectedTimeSlot) {
          const currentSlot = response.data.slots.find(
            slot => slot.startTime === selectedTimeSlot.startTime
          );
          if (!currentSlot || !currentSlot.available) {
            setSelectedTimeSlot(null);
          }
        }
      } else {
        setAvailableSlots([]);
        setError(response.data.reason || "No time slots available for this date");
      }
    } catch (error) {
      console.error("Error loading slots:", error);
      setError("Error loading available time slots");
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const calculatePriceForBooking = (bookingDuration) => {
    if (!service) return 0;

    let basePrice = service.pricing.basePrice || 0;
    let optionPrices = 0;

    // Handle fixed pricing per time slot
    if (service.pricing.pricingType === "fixed") {
      // First, try to find the timeSlot-specific base price from optionPricing
      const timeSlotPrice = service.pricing.optionPricing?.find(
        (pricing) =>
          pricing.fieldId === 'timeSlot' &&
          pricing.optionValue === `${bookingDuration}min`
      );

      if (timeSlotPrice && timeSlotPrice.additionalPrice > 0) {
        // Use the time slot-specific price as the base price
        basePrice = timeSlotPrice.additionalPrice;
      }
      // If no specific time slot price is found, use the service's base price as fallback
    } else if (service.pricing.pricingType === "duration_based") {
      // Duration-based pricing: calculate based on duration
      basePrice = (bookingDuration / 60) * (service.pricing.pricePerMinute || 0);
    }
    // For other pricing types, use the service's base price as is

    // Calculate additional option prices from form responses (excluding timeSlot since we already handled it)
    if (service.pricing.optionPricing && formResponses) {
      Object.entries(formResponses).forEach(([fieldId, responseValue]) => {
        // Skip timeSlot since we already handled it as the base price
        if (fieldId === 'timeSlot') {
          return;
        }

        // Find time-slot-specific pricing first
        const optionPriceWithSlot = service.pricing.optionPricing.find(
          (pricing) =>
            pricing.fieldId === fieldId &&
            pricing.optionValue === responseValue &&
            pricing.timeSlotDuration == bookingDuration  // Use == instead of === to handle type coercion
        );

        // Fall back to general pricing if no time-slot-specific pricing exists
        const optionPriceGeneral = service.pricing.optionPricing.find(
          (pricing) =>
            pricing.fieldId === fieldId &&
            pricing.optionValue === responseValue &&
            !pricing.timeSlotDuration
        );

        const optionPrice = optionPriceWithSlot || optionPriceGeneral;

        if (optionPrice) {
          optionPrices += optionPrice.additionalPrice || 0;
        }
      });
    }

    const subtotal = basePrice + optionPrices;
    const gst = service.hasGST ? subtotal * 0.05 : 0;
    const pst = service.hasPST ? subtotal * 0.07 : 0;
    const totalAmount = subtotal + gst + pst;

    return {
      basePrice,
      optionPrices,
      subtotal,
      gst,
      pst,
      totalAmount,
    };
  };

  const calculateTotalPrice = () => {
    let totalBasePrice = 0;
    let totalOptionPrices = 0;
    let totalSubtotal = 0;
    let totalGst = 0;
    let totalPst = 0;
    let totalAmount = 0;

    if (bookingType === "package" && selectedPackage) {
      // Package pricing - use the base price from localStorage if available to ensure accuracy
      const packageData = localStorage.getItem('selectedBookingPackage');
      let basePrice = selectedPackage.price;

      // If we have stored packageBasePrice, use it instead (more reliable)
      if (packageData) {
        try {
          const stored = JSON.parse(packageData);
          if (stored.packageBasePrice) {
            basePrice = stored.packageBasePrice;
            console.log('Using stored packageBasePrice for calculation:', basePrice);
          }
        } catch (error) {
          console.error('Error reading packageBasePrice:', error);
        }
      }

      totalBasePrice = basePrice;

      // Add extras if selected
      // Don't add car rental cost for Road Test Package since it's already included in the package price
      if (needsCarRental && selectedPackage?.name !== "Road Test Package") {
        totalOptionPrices += 90;
      }
      if (needsRoadTestBooking) {
        totalOptionPrices += 50;
      }

      totalSubtotal = totalBasePrice + totalOptionPrices;
      totalGst = service?.hasGST ? totalSubtotal * 0.05 : 0;
      totalPst = service?.hasPST ? totalSubtotal * 0.07 : 0;
      totalAmount = totalSubtotal + totalGst + totalPst;
    } else if (bookingType === "single") {
      // Single lesson pricing
      selectedBookings.forEach((booking) => {
        const pricing = calculatePriceForBooking(booking.duration);
        totalBasePrice += pricing.basePrice;
        totalOptionPrices += pricing.optionPrices;
        totalSubtotal += pricing.subtotal;
        totalGst += pricing.gst;
        totalPst += pricing.pst;
        totalAmount += pricing.totalAmount;
      });

      // Add extras for single lessons
      if (needsCarRental) {
        totalOptionPrices += 90;
        totalSubtotal += 90;
      }
      if (needsRoadTestBooking) {
        totalOptionPrices += 50;
        totalSubtotal += 50;
      }

      // Recalculate taxes if extras added
      if (needsCarRental || needsRoadTestBooking) {
        totalGst = service?.hasGST ? totalSubtotal * 0.05 : 0;
        totalPst = service?.hasPST ? totalSubtotal * 0.07 : 0;
        totalAmount = totalSubtotal + totalGst + totalPst;
      }
    }

    setCalculatedPrice({
      basePrice: totalBasePrice,
      optionPrices: totalOptionPrices,
      subtotal: totalSubtotal,
      gst: totalGst,
      pst: totalPst,
      totalAmount: totalAmount,
    });
  };

  const addBooking = () => {
    if (!selectedDate || !selectedTimeSlot) {
      setError("Please select a date and time slot");
      return;
    }

    // Check if this booking already exists
    const existingBooking = selectedBookings.find(
      (booking) =>
        booking.date === selectedDate &&
        booking.startTime === selectedTimeSlot.startTime
    );

    if (existingBooking) {
      setError("This date and time combination is already selected");
      return;
    }

    const newBooking = {
      id: Date.now(), // Temporary ID for frontend
      date: selectedDate,
      startTime: selectedTimeSlot.startTime,
      endTime: selectedTimeSlot.endTime,
      duration: duration,
      pricing: calculatePriceForBooking(duration),
    };

    const updatedBookings = [...selectedBookings, newBooking];
    setSelectedBookings(updatedBookings);

    // Update cart with ALL bookings for single lessons
    if (bookingType === "single") {
      try {
        // Calculate total price for all bookings
        const totalPrice = updatedBookings.reduce((sum, booking) => sum + booking.pricing.totalAmount, 0);

        const singleLessonData = {
          class: selectedClass || service.name,
          name: updatedBookings.length === 1 ? "Single Lesson" : `${updatedBookings.length} Lessons`,
          price: totalPrice,
          lessons: updatedBookings.length,
          duration: duration,
          savings: 0,
          description: updatedBookings.map((b, idx) => {
            // Fix timezone issue by parsing the date string correctly
            const [year, month, day] = b.date.split('-');
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            return `Lesson ${idx + 1}: ${b.duration} min on ${date.toLocaleDateString()} at ${b.startTime}`;
          }).join(' | '),
          bookingPageUrl: window.location.pathname,
          bookingType: "single",
          date: updatedBookings[0].date, // Primary date for display
          startTime: updatedBookings[0].startTime, // Primary time for display
          endTime: updatedBookings[0].endTime,
          allBookings: updatedBookings, // Store all bookings
          currentStep: currentStep,
          timestamp: Date.now()
        };

        localStorage.setItem('selectedBookingPackage', JSON.stringify(singleLessonData));
        window.dispatchEvent(new Event('bookingPackageUpdated'));

        console.log('Cart updated with all bookings:', singleLessonData);
      } catch (error) {
        console.error("Error updating cart:", error);
      }
    }

    setSelectedDate("");
    setSelectedTimeSlot(null);
    setShowAddAnother(true);
    setError("");

    // Update cart with package booking data including lesson dates
    if (bookingType === "package" && selectedPackage) {
      try {
        const packageData = {
          class: selectedClass,
          name: selectedPackage.name,
          price: selectedPackage.price,
          packageBasePrice: selectedPackage.price,
          lessons: selectedPackage.lessons,
          duration: selectedPackage.duration,
          savings: selectedPackage.savings,
          description: selectedPackage.description,
          bookingPageUrl: window.location.pathname,
          currentStep: currentStep,
          bookingType: bookingType,
          needsCarRental: needsCarRental,
          needsRoadTestBooking: needsRoadTestBooking,
          // Add lesson dates for display
          date: updatedBookings[0]?.date || null, // Primary date for display
          startTime: updatedBookings[0]?.startTime || null, // Primary time for display
          endTime: updatedBookings[0]?.endTime || null,
          allBookings: updatedBookings, // Store all bookings
          timestamp: Date.now()
        };

        localStorage.setItem('selectedBookingPackage', JSON.stringify(packageData));
        window.dispatchEvent(new Event('bookingPackageUpdated'));

        console.log('Package cart updated with lesson dates:', packageData);
      } catch (error) {
        console.error("Error updating package cart:", error);
      }
    }

    // Check if user needs to book more lessons (for packages)
    const totalLessonsNeeded = selectedPackage?.lessons || 0;
    const lessonsBooked = selectedBookings.length + 1; // +1 for the booking we just added

    // If user needs to book more lessons, scroll to top of date picker
    if (totalLessonsNeeded > 0 && lessonsBooked < totalLessonsNeeded) {
      setTimeout(() => {
        const datePickerSection = document.querySelector('.form-section.active');
        if (datePickerSection) {
          const yOffset = -100;
          const y = datePickerSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
  };

  const removeBooking = (bookingId) => {
    const updatedBookings = selectedBookings.filter((booking) => booking.id !== bookingId);
    setSelectedBookings(updatedBookings);
    setShowAddAnother(false);

    // Update cart for single lesson bookings
    if (bookingType === "single") {
      try {
        if (updatedBookings.length === 0) {
          // No more bookings, clear cart and reset state
          localStorage.removeItem('selectedBookingPackage');
          window.dispatchEvent(new Event('bookingPackageUpdated'));
          console.log('All lessons removed from cart');

          // Reset to step 1 so user can see all booking type options
          setCurrentStep(1);
          setBookingType("");
          setSelectedClass("");
        } else {
          // Update cart with remaining bookings
          const totalPrice = updatedBookings.reduce((sum, booking) => sum + booking.pricing.totalAmount, 0);

          const packageData = localStorage.getItem('selectedBookingPackage');
          if (packageData) {
            const booking = JSON.parse(packageData);
            booking.name = updatedBookings.length === 1 ? "Single Lesson" : `${updatedBookings.length} Lessons`;
            booking.price = totalPrice;
            booking.lessons = updatedBookings.length;
            booking.description = updatedBookings.map((b, idx) => {
              // Fix timezone issue by parsing the date string correctly
              const [year, month, day] = b.date.split('-');
              const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
              return `Lesson ${idx + 1}: ${b.duration} min on ${date.toLocaleDateString()} at ${b.startTime}`;
            }).join(' | ');
            booking.date = updatedBookings[0].date;
            booking.startTime = updatedBookings[0].startTime;
            booking.endTime = updatedBookings[0].endTime;
            booking.allBookings = updatedBookings;

            localStorage.setItem('selectedBookingPackage', JSON.stringify(booking));
            window.dispatchEvent(new Event('bookingPackageUpdated'));
            console.log('Cart updated after removing booking');
          }
        }
      } catch (error) {
        console.error("Error updating cart after removal:", error);
      }
    }

    // Update cart for package bookings
    if (bookingType === "package" && selectedPackage) {
      try {
        if (updatedBookings.length === 0) {
          // No more bookings, clear cart and reset state
          localStorage.removeItem('selectedBookingPackage');
          window.dispatchEvent(new Event('bookingPackageUpdated'));
          console.log('All lessons removed from package cart');

          // Reset to step 1 so user can see all booking type options
          setCurrentStep(1);
          setBookingType("");
          setSelectedClass("");
          setSelectedPackage(null);
        } else {
          // Update cart with remaining bookings
          const packageData = {
            class: selectedClass,
            name: selectedPackage.name,
            price: selectedPackage.price,
            packageBasePrice: selectedPackage.price,
            lessons: selectedPackage.lessons,
            duration: selectedPackage.duration,
            savings: selectedPackage.savings,
            description: selectedPackage.description,
            bookingPageUrl: window.location.pathname,
            currentStep: currentStep,
            bookingType: bookingType,
            needsCarRental: needsCarRental,
            needsRoadTestBooking: needsRoadTestBooking,
            // Add lesson dates for display
            date: updatedBookings[0]?.date || null, // Primary date for display
            startTime: updatedBookings[0]?.startTime || null, // Primary time for display
            endTime: updatedBookings[0]?.endTime || null,
            allBookings: updatedBookings, // Store all bookings
            timestamp: Date.now()
          };

          localStorage.setItem('selectedBookingPackage', JSON.stringify(packageData));
          window.dispatchEvent(new Event('bookingPackageUpdated'));
          console.log('Package cart updated after removing booking');
        }
      } catch (error) {
        console.error("Error updating package cart after removal:", error);
      }
    }
  };

  const handleAddAnother = (response) => {
    setAddAnotherResponse(response);
    if (response === "yes") {
      setShowAddAnother(false);
      setSelectedDate("");
      setSelectedTimeSlot(null);
      // Scroll to the date picker area
      setTimeout(scrollToForm, 100);
    } else {
      // User doesn't want to add another, proceed to next step
      nextStep();
    }
  };

  const handleFormResponseChange = (fieldId, value) => {
    setFormResponses((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const validateForm = () => {
    if (selectedBookings.length === 0) {
      setError("Please select at least one date and time");
      return false;
    }

    if (
      !customerInfo.firstName ||
      !customerInfo.lastName ||
      !customerInfo.email ||
      !customerInfo.phone ||
      !customerInfo.location
    ) {
      setError("Please fill in all required customer information");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerInfo.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (customerInfo.phone && !/^[\d\s\-\+\(\)\.]+$/.test(customerInfo.phone)) {
      setError("Please enter a valid phone number");
      return false;
    }

    const requiredFields = service.formFields.filter((field) => field.required);
    for (const field of requiredFields) {
      const value = formResponses[field.fieldId];
      if (!value || (Array.isArray(value) && value.length === 0)) {
        setError(`Please fill in the required field: ${field.label}`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setBookingLoading(true);
    setError("");

    try {
      let paymentMethodId = null;

      if (service.requiresPayment && calculatedPrice.totalAmount > 0) {
        if (!stripe || !elements) {
          setError("Payment system not ready. Please try again.");
          setBookingLoading(false);
          return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          setError("Payment form not found. Please refresh and try again.");
          setBookingLoading(false);
          return;
        }

        const { error: paymentError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
              name: `${customerInfo.firstName} ${customerInfo.lastName}`,
              email: customerInfo.email,
              phone: customerInfo.phone || undefined,
            },
          });

        if (paymentError) {
          setError(paymentError.message);
          setBookingLoading(false);
          return;
        }

        paymentMethodId = paymentMethod.id;
      }

      const bookingData = {
        serviceSlug: service.slug,
        customerInfo,
        bookingType,
        selectedClass,
        selectedPackage: bookingType === "package" ? selectedPackage : null,
        planId: selectedPackage?.planId || null, // Include plan ID if available
        needsCarRental,
        needsRoadTestBooking,
        bookings: selectedBookings.map((booking) => ({
          bookingDate: booking.date + "T00:00:00",
          startTime: booking.startTime,
          duration: booking.duration,
        })),
        formResponses: Object.entries(formResponses).map(
          ([fieldId, value]) => ({
            fieldId,
            value,
            label:
              service.formFields?.find((f) => f.fieldId === fieldId)?.label ||
              fieldId,
          })
        ),
        paymentMethodId,
      };

      const response = await axios.post("/api/bookings/bulk", bookingData);

      if (response.data.success) {
        setBookingResult(response.data);
        setSuccess(true);
        // Navigate to a summary page or show success message
        navigate(`/booking-confirmation?count=${response.data.bookingCount}&email=${customerInfo.email}`);
      } else {
        setError("Booking failed. Please try again.");
      }
    } catch (error) {
      console.error("Booking error:", error);
      setError(
        error.response?.data?.error ||
        "An error occurred while creating your bookings"
      );
    } finally {
      setBookingLoading(false);
    }
  };

  const renderFormField = (field) => {
    const value = formResponses[field.fieldId] || "";

    switch (field.type) {
      case "text":
      case "email":
      case "phone":
        return (
          <input
            type={field.type}
            value={value}
            onChange={(e) =>
              handleFormResponseChange(field.fieldId, e.target.value)
            }
            placeholder={field.placeholder}
            required={field.required}
            className="form-input"
          />
        );

      case "textarea":
        return (
          <textarea
            value={value}
            onChange={(e) =>
              handleFormResponseChange(field.fieldId, e.target.value)
            }
            placeholder={field.placeholder}
            required={field.required}
            rows="3"
            className="form-input"
          />
        );

      case "select":
        return (
          <select
            value={value}
            onChange={(e) =>
              handleFormResponseChange(field.fieldId, e.target.value)
            }
            required={field.required}
            className="form-input"
          >
            <option value="">Select an option...</option>
            {field.options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "radio":
        return (
          <div className="space-y-2">
            {field.options.map((option, index) => (
              <label key={index} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={field.fieldId}
                  value={option}
                  checked={value === option}
                  onChange={(e) =>
                    handleFormResponseChange(field.fieldId, e.target.value)
                  }
                  required={field.required}
                  className="text-blue-600"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case "checkbox":
        return (
          <div className="space-y-2">
            {field.options.map((option, index) => (
              <label key={index} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  value={option}
                  checked={
                    Array.isArray(value) ? value.includes(option) : false
                  }
                  onChange={(e) => {
                    const currentValues = Array.isArray(value) ? value : [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter((v) => v !== option);
                    handleFormResponseChange(field.fieldId, newValues);
                  }}
                  className="rounded"
                  style={{ accentColor: getConfigColor('primary', '#6366f1') }}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "driving_class":
        return <Car className="w-5 h-5" />;
      case "restaurant":
        return <Utensils className="w-5 h-5" />;
      default:
        return <Calendar className="w-5 h-5" />;
    }
  };

  const formatSelectedDate = (dateString) => {
    if (!dateString) return "Select a date";

    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="text-center">
          <CheckCircle className="w-16 h-16  mx-auto mb-4"
            style={{ color: getConfigColor('success', '#10b981') }} />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Bookings Confirmed!
          </h2>
          <p className="text-gray-600 mb-6">
            Your {bookingResult?.bookingCount || selectedBookings.length} booking(s) have been successfully created.
          </p>
          {bookingResult && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold mb-2">Booking Details</h3>
              <p>
                <strong>Total Bookings:</strong> {bookingResult.bookingCount}
              </p>
              <p>
                <strong>Total Amount:</strong> ${bookingResult.totalAmount?.toFixed(2) || calculatedPrice.totalAmount.toFixed(2)}
              </p>
              <div className="mt-4">
                <h4 className="font-medium mb-2">Booking Numbers:</h4>
                <div className="space-y-1">
                  {bookingResult.bookings?.map((booking, index) => (
                    <p key={index} className="text-sm">
                      {booking.bookingNumber} - {booking.status}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}
          <button
            onClick={() => navigate("/")}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="booking-page"
      style={{
        "--primary": config?.colors?.primary || "#6366f1",
        "--primary-light": config?.colors?.accent || "#8b5cf6",
        "--secondary": config?.colors?.secondary || "#f59e0b",
        "--dark": config?.colors?.textPrimary || "#0f172a",
        "--text-primary": config?.colors?.textPrimary || "#111827",
        "--text-secondary": config?.colors?.textSecondary || "#6b7280",
      }}
    >
      {/* Hero Section */}
      <section
        className="booking-hero"
        style={{
          backgroundImage: service?.heroImage
            ? `url(${service.heroImage})`
            : "var(--gradient-1)",
          marginTop: 0,
        }}
      >
        <div className="booking-hero-content">
          <h1>Book Your {service?.name}</h1>
          <p>
            {service?.description ||
              "Follow the step-by-step process to schedule your perfect experience."}
          </p>
        </div>
      </section>

      {/* Progress Bar */}

      <div className="progress-bar">
        <div className="progress-line">
          <div
            className="progress-line-fill"
            style={{ width: `${getProgressPercentage()}%` }}
          ></div>
        </div>
        {steps.map((step) => (
          <div key={step.id} className="progress-step">
            <div
              className={`step-circle ${currentStep === step.id ? "active" : ""
                } ${isStepComplete(step.id) ? "completed" : ""}`}
            >
              {isStepComplete(step.id) ? "✓" : step.id}
            </div>
            <div
              className={`step-label ${currentStep === step.id ? "active" : ""
                }`}
            >
              {step.label}
            </div>
          </div>
        ))}
      </div>


      {/* Main Content */}
      <div className="booking-container">
        <div className="booking-grid">
          {/* Booking Form */}
          <div className="booking-form">
            {/* Step 1: Booking Type Selection */}
            {currentStep === 1 && (
              <div className="form-section active">
                <h2 className="section-title font-normal text-sm">
                  <div className="section-icon">
                    <FileText className="w-5 h-5" />
                  </div>
                  Choose Booking Type
                </h2>

                <p className="text-gray-600 mb-6">
                  Would you like to book a single lesson or a package? Packages offer better value for multiple lessons.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  {/* Call Us Option */}
                  {/* <a
                    href="tel:8668666630"
                    className="p-6 border-2 rounded-lg transition-all flex flex-col border-gray-300 hover:border-gray-400 block text-center"
                  >
                    <Phone className="w-12 h-12 mx-auto mb-3 text-[#4b5563]" />
                    <h3 className="text-lg font-semibold mb-2">Call Us </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Prefer to speak with someone? Give us a call
                    </p>
                    <p className="text-md font-semibold text-blue-600">
                      (866) 866-6630 <br />
                      <a
                        href="tel:7787585859"
                        className="text-md font-semibold text-blue-600"
                      >
                        (778) 758-5859
                      </a> <br />
                      <a href="https://wa.me/7787585859" className="text-md font-semibold text-blue-600 flex items-center justify-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="inline-block"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        WhatsApp
                      </a>
                    </p>
                  </a> */}

                  {/* Single Lesson Option */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();

                      // Only clear if actually switching from a different type
                      const isSwitchingTypes = bookingType && bookingType !== "single";

                      setBookingType("single");

                      if (isSwitchingTypes) {
                        // Clear all bookings when switching booking type
                        setSelectedBookings([]);
                        setSelectedPackage(null);
                        setSelectedDate("");
                        setSelectedTimeSlot(null);
                        // Clear extras when switching booking type
                        setNeedsCarRental(false);
                        setNeedsRoadTestBooking(false);
                        // Clear cart when switching from package to single
                        try {
                          localStorage.removeItem('selectedBookingPackage');
                          window.dispatchEvent(new Event('bookingPackageUpdated'));
                        } catch (error) {
                          console.error("Error clearing cart:", error);
                        }
                      }

                      // Reset to 60 minutes for single bookings
                      if (duration !== 60) {
                        setDuration(60);
                      }
                      // Auto-scroll to next button after selection
                      scrollToNextButton();
                    }}
                    onDoubleClick={(e) => {
                      e.preventDefault();

                      // Only clear if actually switching from a different type
                      const isSwitchingTypes = bookingType && bookingType !== "single";

                      setBookingType("single");

                      if (isSwitchingTypes) {
                        // Clear all bookings when switching booking type
                        setSelectedBookings([]);
                        setSelectedPackage(null);
                        setSelectedDate("");
                        setSelectedTimeSlot(null);
                        // Clear extras when switching booking type
                        setNeedsCarRental(false);
                        setNeedsRoadTestBooking(false);
                        // Clear cart when switching from package to single
                        try {
                          localStorage.removeItem('selectedBookingPackage');
                          window.dispatchEvent(new Event('bookingPackageUpdated'));
                        } catch (error) {
                          console.error("Error clearing cart:", error);
                        }
                      }

                      // Reset to 60 minutes for single bookings
                      if (duration !== 60) {
                        setDuration(60);
                      }
                      // Auto-advance to next step
                      setTimeout(() => {
                        nextStep();
                        // Use mobile-optimized scroll
                        const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                        if (isMobile) {
                          scrollToNextButton();
                        } else {
                          scrollToForm();
                        }
                      }, 300);
                    }}
                    className={`p-6 border-2 rounded-lg transition-all flex flex-col ${bookingType === "single"
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-300 hover:border-gray-400"
                      }`}
                    style={{
                      borderColor: bookingType === "single" ? getConfigColor('primary', '#6366f1') : '',
                      backgroundColor: bookingType === "single" ? getConfigColorWithOpacity('primary', '10', '#6366f110') : '',
                    }}
                  >
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-[#4b5563]" />
                    <h3 className="text-lg font-semibold mb-2">Single Lesson</h3>
                    <p className="text-sm text-gray-600">
                      Book individual lessons at your own pace
                    </p>
                  </button>

                  {/* Package Option */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();

                      // Only clear if actually switching from a different type
                      const isSwitchingTypes = bookingType && bookingType !== "package";

                      setBookingType("package");

                      if (isSwitchingTypes) {
                        // Clear all bookings when switching booking type
                        setSelectedBookings([]);
                        setSelectedPackage(null);
                        setSelectedDate("");
                        setSelectedTimeSlot(null);
                        // Clear extras when switching booking type
                        setNeedsCarRental(false);
                        setNeedsRoadTestBooking(false);
                        // Clear cart when switching from single to package
                        try {
                          localStorage.removeItem('selectedBookingPackage');
                          window.dispatchEvent(new Event('bookingPackageUpdated'));
                        } catch (error) {
                          console.error("Error clearing cart:", error);
                        }
                      }

                      // Auto-scroll to next button after selection
                      scrollToNextButton();
                    }}
                    onDoubleClick={(e) => {
                      e.preventDefault();

                      // Only clear if actually switching from a different type
                      const isSwitchingTypes = bookingType && bookingType !== "package";

                      setBookingType("package");

                      if (isSwitchingTypes) {
                        // Clear all bookings when switching booking type
                        setSelectedBookings([]);
                        setSelectedPackage(null);
                        setSelectedDate("");
                        setSelectedTimeSlot(null);
                        // Clear extras when switching booking type
                        setNeedsCarRental(false);
                        setNeedsRoadTestBooking(false);
                        // Clear cart when switching from single to package
                        try {
                          localStorage.removeItem('selectedBookingPackage');
                          window.dispatchEvent(new Event('bookingPackageUpdated'));
                        } catch (error) {
                          console.error("Error clearing cart:", error);
                        }
                      }

                      // Auto-advance to next step
                      setTimeout(() => {
                        nextStep();
                        // Use mobile-optimized scroll
                        const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                        if (isMobile) {
                          scrollToNextButton();
                        } else {
                          scrollToForm();
                        }
                      }, 300);
                    }}
                    className={`p-6 border-2 rounded-lg transition-all flex flex-col ${bookingType === "package"
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-300 hover:border-gray-400"
                      }`}
                    style={{
                      borderColor: bookingType === "package" ? getConfigColor('primary', '#6366f1') : '',
                      backgroundColor: bookingType === "package" ? getConfigColorWithOpacity('primary', '10', '#6366f110') : '',
                    }}
                  >
                    <FileText className="w-12 h-12 mx-auto mb-3"
                      style={{ color: getConfigColor('secondary', '#f59e0b') }} />
                    <h3 className="text-lg font-semibold mb-2">Package</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Save money with our lesson packages (3 or 5 lessons)
                    </p>
                    <p className="text-xs font-semibold mb-2"
                      style={{ color: getConfigColor('secondary', '#f59e0b') }}>
                      Save up to $60 on packages!
                    </p>
                    <span className="inline-block bg-green-600 text-white mt-2 px-3 py-1 text-xs font-semibold rounded-full"
                    >
                      Best Value
                    </span>
                  </button>

                </div>

                <div className="flex justify-end mt-6">
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    Next: Class Selection
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Class Selection */}
            {currentStep === 2 && (
              <div className="form-section active">
                <h2 className="section-title font-normal text-sm">
                  <div className="section-icon">
                    <Car className="w-5 h-5" />
                  </div>
                  Choose Your Class
                </h2>

                <p className="text-gray-600 mb-6">
                  Select the type of driving class you need
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {["Class 5", "N", "Class 4"].map((classType) => {
                    // Get the appropriate image/animation based on class type
                    const getClassIcon = (type) => {
                      if (type === "Class 4") return { type: 'gif', src: BusGif };
                      if (type === "Class 5") return { type: 'gif', src: CarGif };
                      if (type === "N") return { type: 'lottie', data: CapitalNAnimation };
                      return { type: 'lottie', data: CapitalNAnimation }; // default
                    };

                    const icon = getClassIcon(classType);

                    return (
                      <button
                        key={classType}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setSelectedClass(classType);
                          // Also update formResponses if there's a "Class" field in the service
                          const classField = service.formFields?.find(
                            (field) => field.label === "Class" || field.fieldId?.toLowerCase() === "class"
                          );
                          if (classField) {
                            handleFormResponseChange(classField.fieldId, classType);
                          }
                          // Auto-scroll to next button after selection
                          scrollToNextButton();
                        }}
                        onDoubleClick={(e) => {
                          e.preventDefault();
                          setSelectedClass(classType);
                          // Also update formResponses if there's a "Class" field in the service
                          const classField = service.formFields?.find(
                            (field) => field.label === "Class" || field.fieldId?.toLowerCase() === "class"
                          );
                          if (classField) {
                            handleFormResponseChange(classField.fieldId, classType);
                          }
                          // Auto-advance to next step
                          setTimeout(() => {
                            nextStep();
                            // Use mobile-optimized scroll
                            const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                            if (isMobile) {
                              scrollToNextButton();
                            } else {
                              scrollToForm();
                            }
                          }, 300);
                        }}
                        className={`p-6 border-2 rounded-lg transition-all ${selectedClass === classType
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300 hover:border-gray-400"
                          }`}
                        style={{
                          borderColor: selectedClass === classType ? getConfigColor('primary', '#6366f1') : '',
                          backgroundColor: selectedClass === classType ? getConfigColorWithOpacity('primary', '10', '#6366f110') : '',
                        }}
                      >
                        {classType === "Class 4" && (
                          <div className="relative px-3 py-1 rounded-full text-white font-bold text-xs shadow-lg bg-red-600 w-fit -mb-6" style={{ top: '-1.25rem', left: '-1.2rem' }}>
                            Restricted
                          </div>
                        )}
                        <div className="w-32 mx-auto flex items-center justify-center overflow-hidden">
                          {icon.type === 'gif' ? (
                            <img
                              src={icon.src}
                              alt={classType}
                              style={{ width: '60%', height: '60%', objectFit: 'contain' }}
                            />
                          ) : (
                            <Lottie
                              animationData={icon.data}
                              loop={true}
                              autoplay={true}
                              style={{ width: '60%', height: '60%' }}
                              rendererSettings={{
                                preserveAspectRatio: 'xMidYMid meet'
                              }}
                            />
                          )}
                        </div>
                        <h3 className="text-lg font-semibold">{classType === "N" ? "Class N" : classType}</h3>
                      </button>
                    );
                  })}
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    {bookingType === "single" ? "Next: Date & Time" : "Next: Select Package"}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Date & Time Selection (for Single Lessons) OR Package Selection (for Packages) */}
            {currentStep === 3 && bookingType === "single" && (
              <div className="form-section active">
                <h2 className="section-title font-normal text-sm">
                  <div className="section-icon">
                    <Calendar className="w-5 h-5" />
                  </div>
                  Select Date & Time
                </h2>

                {/* Selected Bookings Summary */}
                {selectedBookings.length > 0 && (
                  <div
                    className="mb-6 p-4 rounded-lg border"
                    style={{
                      backgroundColor: getConfigColorWithOpacity('success', '10', '#10b98110'),
                      borderColor: getConfigColorWithOpacity('success', '20', '#10b98120')
                    }}
                  >
                    <h3
                      className="font-semibold mb-3"
                      style={{ color: getConfigColor('success', '#10b981') }}
                    >
                      Selected Bookings ({selectedBookings.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedBookings.map((booking) => (
                        <div
                          key={booking.id}
                          className="flex items-center justify-between p-3 bg-white rounded border"
                        >
                          <div>
                            <p className="font-medium">
                              {formatSelectedDate(booking.date)}
                            </p>
                            <p className="text-sm text-gray-600">
                              {booking.startTime} - {booking.endTime} ({booking.duration} min)
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeBooking(booking.id)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Package Suggestion for 3 or 5 bookings (only for 90-min lessons) */}
                {(selectedBookings.length === 3 || selectedBookings.length === 5) &&
                  selectedBookings.every(booking => booking.duration === 90) && (
                    <div
                      className="mb-6 p-4 rounded-lg border-2 animate-pulse"
                      style={{
                        backgroundColor: getConfigColorWithOpacity('secondary', '10', '#f59e0b10'),
                        borderColor: getConfigColor('secondary', '#f59e0b')
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: getConfigColor('secondary', '#f59e0b') }}>
                          <Info className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-lg mb-2"
                            style={{ color: getConfigColor('secondary', '#f59e0b') }}>
                            💡 Save Money with a Package!
                          </h3>
                          <p className="text-sm mb-3 text-gray-700">
                            You've selected {selectedBookings.length} lessons. Did you know you could save{' '}
                            <strong className="font-bold"
                              style={{ color: getConfigColor('secondary', '#f59e0b') }}>
                              {selectedClass === "Class 4"
                                ? selectedBookings.length === 3 ? "$60" : "$50"
                                : selectedBookings.length === 3 ? "$30" : "$50"
                              }
                            </strong>
                            {' '}by choosing our {selectedBookings.length}-lesson package for {selectedClass || "your class"}?
                          </p>
                          <div className="flex gap-3">
                            <button
                              type="button"
                              onClick={() => {
                                // Switch to package booking
                                setBookingType("package");
                                setCurrentStep(3); // Go to package selection
                                setSelectedBookings([]); // Clear single bookings
                                setSelectedDate("");
                                setSelectedTimeSlot(null);
                                // Clear extras when switching booking type
                                setNeedsCarRental(false);
                                setNeedsRoadTestBooking(false);
                                scrollToForm();
                              }}
                              className="px-4 py-2 rounded-lg text-white font-semibold shadow-md hover:shadow-lg transition-all"
                              style={{ backgroundColor: getConfigColor('secondary', '#f59e0b') }}
                            >
                              Switch to Package & Save{' '}
                              {selectedClass === "Class 4"
                                ? selectedBookings.length === 3 ? "$60" : "$50"
                                : selectedBookings.length === 3 ? "$30" : "$50"
                              }
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                // Dismiss suggestion (do nothing)
                              }}
                              className="px-4 py-2 rounded-lg border-2 font-semibold hover:bg-gray-50 transition-all"
                              style={{
                                borderColor: getConfigColor('secondary', '#f59e0b'),
                                color: getConfigColor('secondary', '#f59e0b')
                              }}
                            >
                              Continue with Single Lessons
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Available Days Info */}
                {service?.availableDays && service.availableDays.length > 0 && (
                  <div
                    className="mb-4 p-3 rounded-lg"
                    style={{
                      backgroundColor: getConfigColorWithOpacity('primary', '10', '#6366f110'),
                      borderColor: getConfigColorWithOpacity('primary', '20', '#6366f120')
                    }}
                  >
                    <div className="flex items-start">
                      <Info
                        className="w-4 h-4 mr-2 mt-0.5"
                        style={{ color: getConfigColor('primary', '#6366f1') }}
                      />
                      <div>
                        <p
                          className="text-sm font-medium"
                          style={{ color: getConfigColor('primary', '#6366f1') }}
                        >
                          Available Days:
                        </p>
                        <p
                          className="text-sm"
                          style={{ color: getConfigColor('primary', '#6366f1') }}
                        >
                          {service.availableDays
                            .map(
                              (day) =>
                                day.charAt(0).toUpperCase() + day.slice(1)
                            )
                            .join(", ")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-4">
                  {availableDurations.length > 1 && (
                    <div>
                      <label className="form-label">Duration (minutes) *</label>
                      <select
                        value={duration}
                        onChange={(e) => setDuration(parseInt(e.target.value))}
                        className="form-input"
                      >
                        {availableDurations.map((dur) => (
                          <option key={dur} value={dur}>
                            {dur} minutes ({(dur / 60).toFixed(1)} hours)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="form-label">Date *</label>
                    <CustomDatePicker
                      selectedDate={selectedDate}
                      onDateChange={setSelectedDate}
                      minDate={getMinDate()}
                      maxDate={getMaxDate()}
                      availableDays={service?.availableDays || []}
                      config={config}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Available {service?.minNoticeHours}+ hours in advance
                    </p>
                  </div>
                </div>

                {/* Available Time Slots */}
                {selectedDate && (
                  <div id="time-slots-section" className="scroll-target">
                    <label className="form-label">Time Slots *</label>
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2
                          className="w-6 h-6 animate-spin"
                          style={{ color: getConfigColor('primary', '#6366f1') }}
                        />
                        <span className="ml-2">Loading available slots...</span>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="time-grid">
                        {availableSlots.map((slot, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => slot.available && setSelectedTimeSlot(slot)}
                            disabled={!slot.available}
                            title={slot.available ? '' : slot.reason}
                            className={`time-card ${selectedTimeSlot?.startTime === slot.startTime
                              ? "selected"
                              : ""
                              } ${!slot.available
                                ? "disabled opacity-50 cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200"
                                : "hover:bg-blue-50 cursor-pointer"
                              }`}
                            style={{
                              backgroundColor: !slot.available ? '#f3f4f6' : '',
                              color: !slot.available ? '#9ca3af' : '',
                              borderColor: !slot.available ? '#e5e7eb' : ''
                            }}
                          >
                            <div className="font-weight-600 text-lg">
                              {slot.startTime}
                            </div>
                            <div className="text-xs opacity-75">
                              to {slot.endTime}
                            </div>
                            {!slot.available && slot.reason && (
                              <div className="text-xs mt-1 text-red-500 truncate">
                                {slot.reason}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                        <Clock className="w-8 h-8 mx-auto mb-2" />
                        <p>{error || "No time slots available for this date"}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Add Booking Button */}
                {selectedDate && selectedTimeSlot && (
                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={addBooking}
                      className="text-white px-6 py-2 rounded-lg transition-colors"
                      style={{
                        backgroundColor: getConfigColor('success', '#10b981'),
                        ':hover': {
                          backgroundColor: getConfigColor('success', '#10b981')
                        }
                      }}
                    >
                      Add This Booking
                    </button>
                  </div>
                )}

                {/* Add Another Booking Question */}
                {showAddAnother && (
                  <div
                    className="mt-6 p-4 rounded-lg border"
                    style={{
                      backgroundColor: getConfigColorWithOpacity('primary', '10', '#6366f110'),
                      borderColor: getConfigColorWithOpacity('primary', '20', '#6366f120')
                    }}
                  >
                    <h3
                      className="font-semibold mb-3"
                      style={{ color: getConfigColor('primary', '#6366f1') }}
                    >
                      Would you like to add another date and time?
                    </h3>
                    <p
                      className="mb-4"
                      style={{ color: getConfigColor('primary', '#6366f1') }}
                    >
                      You can select as many dates and times as you need for your bookings.
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => handleAddAnother("yes")}
                        className="text-white px-4 py-2 rounded-lg"
                        style={{ backgroundColor: getConfigColor('primary', '#6366f1') }}
                      >
                        Yes, Add Another
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAddAnother("no")}
                        className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                      >
                        No, Continue
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    Next: Customer Information
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Package Selection (for Packages) */}
            {currentStep === 3 && bookingType === "package" && (
              <div className="form-section active">
                <h2 className="section-title font-normal text-sm">
                  <div className="section-icon">
                    <FileText className="w-5 h-5" />
                  </div>
                  Choose Your Package
                </h2>

                <p className="text-gray-600 mb-6">
                  Select the package that best fits your needs for {selectedClass}
                </p>

                {plansLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2
                      className="w-6 h-6 animate-spin"
                      style={{ color: getConfigColor('primary', '#6366f1') }}
                    />
                    <span className="ml-2">Loading available plans...</span>
                  </div>
                ) : !packageOptions[selectedClass] || packageOptions[selectedClass].length === 0 ? (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-lg font-medium mb-2">No plans available</p>
                    <p className="text-sm">
                      There are currently no plans available for {selectedClass}.
                      Please contact us or select a different class.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {packageOptions[selectedClass].map((pkg, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          setSelectedPackage(pkg);
                          // Auto-set car rental for Road Test Package
                          if (pkg.name === "Road Test Package") {
                            setNeedsCarRental(true);
                          }
                          // Add package to cart for display in nav
                          addPackageToCart(pkg);
                          // Auto-scroll to next button after selection
                          scrollToNextButton();
                        }}
                        onDoubleClick={() => {
                          setSelectedPackage(pkg);
                          // Auto-set car rental for Road Test Package
                          if (pkg.name === "Road Test Package") {
                            setNeedsCarRental(true);
                          }
                          // Add package to cart for display in nav
                          addPackageToCart(pkg);
                          if (canGoNext()) {
                            nextStep();
                          }
                        }}
                        className={`p-6 flex flex-col justify-start border-2 rounded-lg transition-all text-left relative overflow-visible ${selectedPackage?.name === pkg.name
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300 hover:border-gray-400"
                          }`}
                        style={{
                          borderColor: selectedPackage?.name === pkg.name ? getConfigColor('primary', '#6366f1') : '',
                          backgroundColor: selectedPackage?.name === pkg.name ? getConfigColorWithOpacity('primary', '10', '#6366f110') : '',
                        }}
                      >

                        {pkg.savings > 0 && (
                          <div className="absolute -top-3 right-2 px-3 py-1 rounded-full text-white font-bold text-sm shadow-lg z-10"
                            style={{ backgroundColor: getConfigColor('secondary', '#f59e0b') }}>
                            Save ${pkg.savings}
                          </div>
                        )}
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="text-xl font-bold">{pkg.name}</h3>
                            {pkg.savings > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                Regular: ${pkg.price + pkg.savings}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-bold"
                              style={{ color: getConfigColor('primary', '#6366f1') }}>
                              ${pkg.price}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{pkg.description}</p>
                        <div className="text-sm text-gray-700 mt-3">
                          <div className="flex items-center gap-2 mb-1">
                            <CheckCircle className="w-4 h-4" style={{ color: getConfigColor('success', '#10b981') }} />
                            <span>{pkg.lessons} lesson{pkg.lessons > 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex items-center gap-2 mb-1">
                            <Clock className="w-4 h-4" style={{ color: getConfigColor('success', '#10b981') }} />
                            <span>{pkg.duration} minutes per lesson</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" style={{ color: getConfigColor('success', '#10b981') }} />
                            <span>Free pick up & drop off</span>
                          </div>
                        </div>
                        {pkg.lessons > 1 && (
                          <div className="mt-3 pt-3 border-t">
                            <span className="text-xs font-semibold px-2 py-1 rounded"
                              style={{
                                backgroundColor: getConfigColorWithOpacity('success', '20', '#10b98120'),
                                color: getConfigColor('success', '#10b981')
                              }}>
                              ${(pkg.price / pkg.lessons).toFixed(2)} per lesson
                            </span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Set duration from package when moving to next step
                      if (selectedPackage) {
                        setDuration(selectedPackage.duration);
                      }
                      nextStep();
                    }}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    Next: Schedule Lessons
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Schedule Lessons (for Package) - Date & Time Selection */}
            {currentStep === 4 && bookingType === "package" && (
              <div className="form-section active">
                <h2 className="section-title font-normal text-sm">
                  <div className="section-icon">
                    <Calendar className="w-5 h-5" />
                  </div>
                  Schedule Your {selectedPackage?.lessons} Lessons
                </h2>

                <p className="text-gray-600 mb-6">
                  Book the date and time for each lesson in your package. Each lesson is {selectedPackage?.duration} minutes.
                </p>

                {/* Selected Bookings Summary */}
                {selectedBookings.length > 0 && (
                  <div
                    className="mb-6 p-4 rounded-lg border"
                    style={{
                      backgroundColor: getConfigColorWithOpacity('success', '10', '#10b98110'),
                      borderColor: getConfigColorWithOpacity('success', '20', '#10b98120')
                    }}
                  >
                    <h3
                      className="font-semibold mb-3"
                      style={{ color: getConfigColor('success', '#10b981') }}
                    >
                      Scheduled Lessons ({selectedBookings.length}/{selectedPackage?.lessons})
                    </h3>
                    <div className="space-y-2">
                      {selectedBookings.map((booking, index) => (
                        <div
                          key={booking.id}
                          className="flex items-center justify-between p-3 bg-white rounded border"
                        >
                          <div>
                            <p className="font-medium">
                              Lesson {index + 1}: {formatSelectedDate(booking.date)}
                            </p>
                            <p className="text-sm text-gray-600">
                              {booking.startTime} - {booking.endTime} ({booking.duration} min)
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeBooking(booking.id)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedBookings.length < selectedPackage?.lessons && (
                  <>
                    {/* Available Days Info */}
                    {service?.availableDays && service.availableDays.length > 0 && (
                      <div
                        className="mb-4 p-3 rounded-lg"
                        style={{
                          backgroundColor: getConfigColorWithOpacity('primary', '10', '#6366f110'),
                          borderColor: getConfigColorWithOpacity('primary', '20', '#6366f120')
                        }}
                      >
                        <div className="flex items-start">
                          <Info
                            className="w-4 h-4 mr-2 mt-0.5"
                            style={{ color: getConfigColor('primary', '#6366f1') }}
                          />
                          <div>
                            <p
                              className="text-sm font-medium"
                              style={{ color: getConfigColor('primary', '#6366f1') }}
                            >
                              Available Days:
                            </p>
                            <p
                              className="text-sm"
                              style={{ color: getConfigColor('primary', '#6366f1') }}
                            >
                              {service.availableDays
                                .map(
                                  (day) =>
                                    day.charAt(0).toUpperCase() + day.slice(1)
                                )
                                .join(", ")}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-4 mb-4">
                      <div>
                        <label className="form-label">Select Date for Lesson {selectedBookings.length + 1} *</label>
                        <CustomDatePicker
                          selectedDate={selectedDate}
                          onDateChange={setSelectedDate}
                          minDate={getMinDate()}
                          maxDate={getMaxDate()}
                          availableDays={service?.availableDays || []}
                          config={config}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Available {service?.minNoticeHours}+ hours in advance
                        </p>
                      </div>
                    </div>

                    {/* Available Time Slots */}
                    {selectedDate && (
                      <div id="time-slots-section" className="scroll-target">
                        <label className="form-label">Time Slots *</label>
                        {loading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2
                              className="w-6 h-6 animate-spin"
                              style={{ color: getConfigColor('primary', '#6366f1') }}
                            />
                            <span className="ml-2">Loading available slots...</span>
                          </div>
                        ) : availableSlots.length > 0 ? (
                          <div className="time-grid">
                            {availableSlots.map((slot, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => slot.available && setSelectedTimeSlot(slot)}
                                disabled={!slot.available}
                                title={slot.available ? '' : slot.reason}
                                className={`time-card ${selectedTimeSlot?.startTime === slot.startTime
                                  ? "selected"
                                  : ""
                                  } ${!slot.available
                                    ? "disabled opacity-50 cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200"
                                    : "hover:bg-blue-50 cursor-pointer"
                                  }`}
                                style={{
                                  backgroundColor: !slot.available ? '#f3f4f6' : '',
                                  color: !slot.available ? '#9ca3af' : '',
                                  borderColor: !slot.available ? '#e5e7eb' : ''
                                }}
                              >
                                <div className="font-weight-600 text-lg">
                                  {slot.startTime}
                                </div>
                                <div className="text-xs opacity-75">
                                  to {slot.endTime}
                                </div>
                                {!slot.available && slot.reason && (
                                  <div className="text-xs mt-1 text-red-500 truncate">
                                    {slot.reason}
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                            <Clock className="w-8 h-8 mx-auto mb-2" />
                            <p>{error || "No time slots available for this date"}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Add Booking Button */}
                    {selectedDate && selectedTimeSlot && (
                      <div className="mt-6">
                        <button
                          type="button"
                          onClick={addBooking}
                          className="text-white px-6 py-2 rounded-lg transition-colors"
                          style={{
                            backgroundColor: getConfigColor('success', '#10b981'),
                          }}
                        >
                          Add Lesson {selectedBookings.length + 1}
                        </button>
                      </div>
                    )}
                  </>
                )}

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    Next: Customer Information
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Customer Information (for Single) */}
            {currentStep === 4 && bookingType === "single" && (
              <div className="form-section active">
                <h2 className="section-title">
                  <div className="section-icon">
                    <User className="w-5 h-5" />
                  </div>
                  Customer Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      value={customerInfo.firstName}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          firstName: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      value={customerInfo.lastName}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          lastName: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          email: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Phone *</label>
                    <input
                      type="tel"
                      value={customerInfo.phone}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          phone: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group md:col-span-2">
                    <label className="form-label">At which ICBC Location Do You Want to Pick Up and Drop Off? *
                    </label>
                    <select
                      value={customerInfo.location}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          location: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    >
                      <option value="">
                        {locationsLoading ? 'Loading locations...' : 'Select an ICBC location'}
                      </option>
                      {icbcLocations.map((location) => (
                        <option key={location.key} value={location.key}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                    {customerInfo.location && (
                      <p className="text-sm text-gray-600 mt-2">
                        Note: Pick up and drop off will be at the same area
                      </p>
                    )}
                  </div>


                </div>

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    {bookingType === "single" ? "Next: Extras" : "Next: Payment"}
                  </button>
                </div>
              </div>
            )}

            {/* Step 5: Extras (for Single Lessons only) - Road Test Extras */}
            {currentStep === 5 && bookingType === "single" && (
              <div className="form-section active">
                <h2 className="section-title">
                  <div className="section-icon">
                    <FileText className="w-5 h-5" />
                  </div>
                  Additional Services
                </h2>

                <p className="text-gray-600 mb-6">
                  Would you like to add any of these services to your booking?
                </p>

                <div className="space-y-4">
                  {/* Car Rental Option */}
                  <div
                    className="p-4 border-2 rounded-lg transition-all cursor-pointer"
                    onClick={() => setNeedsCarRental(!needsCarRental)}
                    style={{
                      borderColor: needsCarRental ? getConfigColor('primary', '#6366f1') : '#d1d5db',
                      backgroundColor: needsCarRental ? getConfigColorWithOpacity('primary', '10', '#6366f110') : 'white',
                    }}
                  >
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={needsCarRental}
                        onChange={(e) => setNeedsCarRental(e.target.checked)}
                        className="mt-1 mr-3"
                        style={{ accentColor: getConfigColor('primary', '#6366f1') }}
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-lg font-semibold">Car Rental for Road Test</h3>
                          <span className="text-xl font-bold"
                            style={{ color: getConfigColor('primary', '#6366f1') }}>
                            +$90
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Rent a car for your road test. Perfect if you don't have access to a suitable vehicle.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Road Test Booking Option */}
                  {/* <div
                    className="p-4 border-2 rounded-lg transition-all cursor-pointer"
                    onClick={() => setNeedsRoadTestBooking(!needsRoadTestBooking)}
                    style={{
                      borderColor: needsRoadTestBooking ? getConfigColor('primary', '#6366f1') : '#d1d5db',
                      backgroundColor: needsRoadTestBooking ? getConfigColorWithOpacity('primary', '10', '#6366f110') : 'white',
                    }}
                  >
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={needsRoadTestBooking}
                        onChange={(e) => setNeedsRoadTestBooking(e.target.checked)}
                        className="mt-1 mr-3"
                        style={{ accentColor: getConfigColor('primary', '#6366f1') }}
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-lg font-semibold">Book My Road Test</h3>
                          <span className="text-xl font-bold"
                            style={{ color: getConfigColor('primary', '#6366f1') }}>
                            +$50
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          We'll handle booking your road test appointment for you. Save time and hassle!
                        </p>
                      </div>
                    </div>
                  </div> */}
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    Next: Payment
                  </button>
                </div>
              </div>
            )}

            {/* OLD Step 3: Custom Form Fields - REMOVED */}
            {false && currentStep === 3 && (
              <div className="form-section active">
                <h2 className="section-title">
                  <div className="section-icon">
                    <FileText className="w-5 h-5" />
                  </div>
                  Additional Information
                </h2>

                {service?.formFields && service.formFields.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {service.formFields.map((field) => (
                      <div
                        key={field.fieldId}
                        className={`form-group ${field.type === "textarea" ? "md:col-span-2" : ""
                          }`}
                      >
                        <label className="form-label">
                          {field.label}{" "}
                          {field.required && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                        {renderFormField(field)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-8 h-8 mx-auto mb-2" />
                    <p>No additional information required.</p>
                  </div>
                )}

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    {service?.requiresPayment && calculatedPrice.totalAmount > 0
                      ? "Next: Payment"
                      : "Complete Booking"}
                  </button>
                </div>
              </div>
            )}

            {/* Step 6: Payment (for Single Lessons) */}
            {currentStep === 6 && bookingType === "single" &&
              service?.requiresPayment &&
              calculatedPrice.totalAmount > 0 && (
                <div className="form-section active">
                  <h2 className="section-title">
                    <div className="section-icon">
                      <CreditCard className="w-5 h-5" />
                    </div>
                    Payment Information
                  </h2>

                  <div className="max-w-md">
                    <div className="mb-3">
                      <label className="form-label">Card Details</label>
                    </div>
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: "16px",
                            color: "#424770",
                            "::placeholder": {
                              color: "#aab7c4",
                            },
                          },
                        },
                      }}
                      className="p-3 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Your payment information is secure and encrypted.
                    </p>
                  </div>

                  <div className="flex justify-between mt-6">
                    <button
                      type="button"
                      onClick={prevStep}
                      className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={bookingLoading}
                      className="submit-btn"
                    >
                      {bookingLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="w-5 h-5 mr-2" />
                      )}
                      {bookingLoading ? "Processing..." : "Complete Booking"}
                    </button>
                  </div>
                </div>
              )}

            {/* Step 5: Customer Information (for Package) */}
            {currentStep === 5 && bookingType === "package" && (
              <div className="form-section active">
                <h2 className="section-title">
                  <div className="section-icon">
                    <User className="w-5 h-5" />
                  </div>
                  Customer Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      value={customerInfo.firstName}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          firstName: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      value={customerInfo.lastName}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          lastName: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          email: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Phone *</label>
                    <input
                      type="tel"
                      value={customerInfo.phone}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          phone: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group md:col-span-2">
                    <label className="form-label">At which ICBC Location Do You Want to Pick Up and Drop Off? *</label>
                    <select
                      value={customerInfo.location}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          location: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    >
                      <option value="">
                        {locationsLoading ? 'Loading locations...' : 'Select an ICBC location'}
                      </option>
                      {icbcLocations.map((location) => (
                        <option key={location.key} value={location.key}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>


                </div>

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    Next: Payment
                  </button>
                </div>
              </div>
            )}

            {/* Step 6: Extras (for Packages) - Skip for Road Test Package */}
            {currentStep === 6 && bookingType === "package" && selectedPackage?.name !== "Road Test Package" && (
              <div className="form-section active">
                <h2 className="section-title">
                  <div className="section-icon">
                    <FileText className="w-5 h-5" />
                  </div>
                  Additional Services
                </h2>

                <p className="text-gray-600 mb-6">
                  Would you like to add any of these services to your booking?
                </p>

                <div className="space-y-4">
                  {/* Car Rental Option */}
                  <div
                    className="p-4 border-2 rounded-lg transition-all cursor-pointer"
                    onClick={() => setNeedsCarRental(!needsCarRental)}
                    style={{
                      borderColor: needsCarRental ? getConfigColor('primary', '#6366f1') : '#d1d5db',
                      backgroundColor: needsCarRental ? getConfigColorWithOpacity('primary', '10', '#6366f110') : 'white',
                    }}
                  >
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={needsCarRental}
                        onChange={(e) => setNeedsCarRental(e.target.checked)}
                        className="mt-1 mr-3"
                        style={{ accentColor: getConfigColor('primary', '#6366f1') }}
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-lg font-semibold">Car Rental for Road Test</h3>
                          <span className="text-xl font-bold"
                            style={{ color: getConfigColor('primary', '#6366f1') }}>
                            +$90
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Rent a car for your road test. Perfect if you don't have access to a suitable vehicle.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Road Test Booking Option - Only show if NOT Road Test Package */}
                  {/* {selectedPackage?.name !== "Road Test Package" && (
                    <div
                      className="p-4 border-2 rounded-lg transition-all cursor-pointer"
                      onClick={() => setNeedsRoadTestBooking(!needsRoadTestBooking)}
                      style={{
                        borderColor: needsRoadTestBooking ? getConfigColor('primary', '#6366f1') : '#d1d5db',
                        backgroundColor: needsRoadTestBooking ? getConfigColorWithOpacity('primary', '10', '#6366f110') : 'white',
                      }}
                    >
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          checked={needsRoadTestBooking}
                          onChange={(e) => setNeedsRoadTestBooking(e.target.checked)}
                          className="mt-1 mr-3"
                          style={{ accentColor: getConfigColor('primary', '#6366f1') }}
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="text-lg font-semibold">Book My Road Test</h3>
                            <span className="text-xl font-bold"
                              style={{ color: getConfigColor('primary', '#6366f1') }}>
                              +$50
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            We'll handle booking your road test appointment for you. Save time and hassle!
                          </p>
                        </div>
                      </div>
                    </div>
                  )} */}
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    Next: Payment
                  </button>
                </div>
              </div>
            )}

            {/* Step 7: Payment (for Package) */}
            {currentStep === 7 && bookingType === "package" &&
              service?.requiresPayment &&
              calculatedPrice.totalAmount > 0 && (
                <div className="form-section active">
                  <h2 className="section-title">
                    <div className="section-icon">
                      <CreditCard className="w-5 h-5" />
                    </div>
                    Payment Information
                  </h2>

                  <div className="max-w-md">
                    <div className="mb-3">
                      <label className="form-label">Card Details</label>
                    </div>
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: "16px",
                            color: "#424770",
                            "::placeholder": {
                              color: "#aab7c4",
                            },
                          },
                        },
                      }}
                      className="p-3 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Your payment information is secure and encrypted.
                    </p>
                  </div>

                  <div className="flex justify-between mt-6">
                    <button
                      type="button"
                      onClick={prevStep}
                      className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={bookingLoading}
                      className="submit-btn"
                    >
                      {bookingLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="w-5 h-5 mr-2" />
                      )}
                      {bookingLoading ? "Processing..." : "Complete Booking"}
                    </button>
                  </div>
                </div>
              )}

            {/* Complete booking for free services (Single - Step 6) */}
            {currentStep === 6 && bookingType === "single" &&
              (!service?.requiresPayment ||
                calculatedPrice.totalAmount === 0) && (
                <div className="form-section active">
                  <h2 className="section-title">
                    <div className="section-icon">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    Complete Booking
                  </h2>

                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4"
                      style={{ color: getConfigColor('success', '#10b981') }} />
                    <p className="text-lg text-gray-700 mb-6">
                      Review your booking details and complete your reservation.
                    </p>
                  </div>

                  <div className="flex justify-between mt-6">
                    <button
                      type="button"
                      onClick={prevStep}
                      className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={bookingLoading}
                      className="submit-btn"
                    >
                      {bookingLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="w-5 h-5 mr-2" />
                      )}
                      {bookingLoading ? "Processing..." : "Complete Booking"}
                    </button>
                  </div>
                </div>
              )}

            {/* Complete booking for free services (Package - Step 7) */}
            {currentStep === 7 && bookingType === "package" &&
              (!service?.requiresPayment ||
                calculatedPrice.totalAmount === 0) && (
                <div className="form-section active">
                  <h2 className="section-title">
                    <div className="section-icon">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    Complete Booking
                  </h2>

                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4"
                      style={{ color: getConfigColor('success', '#10b981') }} />
                    <p className="text-lg text-gray-700 mb-6">
                      Review your booking details and complete your reservation.
                    </p>
                  </div>

                  <div className="flex justify-between mt-6">
                    <button
                      type="button"
                      onClick={prevStep}
                      className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={bookingLoading}
                      className="submit-btn"
                    >
                      {bookingLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="w-5 h-5 mr-2" />
                      )}
                      {bookingLoading ? "Processing..." : "Complete Booking"}
                    </button>
                  </div>
                </div>
              )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start mt-4">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-red-700">{error}</span>
              </div>
            )}
          </div>

          {/* Booking Summary */}
          <div className={`booking-summary ${isMobile ? 'hidden' : ''}`}>
            <h2 className="summary-title">Booking Summary</h2>

            <div className={`summary-item ${service?.name ? "filled" : ""}`}>
              <span className="summary-label">Service:</span>
              <span className="summary-value">
                {service?.name || "Select a service"}
              </span>
            </div>

            <div className={`summary-item ${bookingType ? "filled" : ""}`}>
              <span className="summary-label">Booking Type:</span>
              <span className="summary-value">
                {bookingType === "single" ? "Single Lesson" : bookingType === "package" ? "Package" : "Not selected"}
              </span>
            </div>

            {selectedClass && (
              <div className="summary-item filled">
                <span className="summary-label">Class:</span>
                <span className="summary-value">{selectedClass}</span>
              </div>
            )}

            {bookingType === "package" && selectedPackage && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="font-medium mb-3">Selected Package:</h4>
                <div className="text-sm bg-gray-50 p-3 rounded">
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-semibold text-base">{selectedPackage.name}</div>
                    {selectedPackage.savings > 0 && (
                      <span className="text-xs font-bold px-2 py-1 rounded-full text-white"
                        style={{ backgroundColor: getConfigColor('secondary', '#f59e0b') }}>
                        Save ${selectedPackage.savings}
                      </span>
                    )}
                  </div>
                  <div className="text-gray-600 mb-1">{selectedPackage.lessons} lessons</div>
                  <div className="text-gray-600 mb-1">{selectedPackage.duration} min per lesson</div>
                  {selectedPackage.savings > 0 && (
                    <div className="text-gray-500 text-xs mb-1">
                      <span className="line-through">Regular: ${selectedPackage.price + selectedPackage.savings}</span>
                    </div>
                  )}
                  <div className="font-bold text-lg mt-2" style={{ color: getConfigColor('primary', '#6366f1') }}>
                    ${selectedPackage.price}
                  </div>
                </div>
              </div>
            )}

            {bookingType === "single" && selectedBookings.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="font-medium mb-3">Selected Bookings ({selectedBookings.length}):</h4>
                <div className="space-y-2">
                  {selectedBookings.map((booking, index) => (
                    <div key={booking.id} className="text-sm bg-gray-50 p-2 rounded">
                      <div className="font-medium">
                        Booking {index + 1}:
                      </div>
                      <div>{formatSelectedDate(booking.date)}</div>
                      <div>{booking.startTime} - {booking.endTime}</div>
                      <div className="text-xs text-gray-600">
                        Duration: {booking.duration} minutes
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Show extras if selected */}
            {(needsCarRental || needsRoadTestBooking) && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="font-medium mb-3">Additional Services:</h4>
                <div className="space-y-2 text-sm">
                  {needsCarRental && (
                    <div className="flex justify-between">
                      <span>Car Rental for Road Test</span>
                      <span className="font-semibold">+$90</span>
                    </div>
                  )}
                  {needsRoadTestBooking && (
                    <div className="flex justify-between">
                      <span>Book My Road Test</span>
                      <span className="font-semibold">+$50</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {service?.requiresPayment && (
              <div className="summary-item price">
                <span className="summary-label">Total Price:</span>
                <span className="summary-value">
                  ${calculatedPrice.totalAmount.toFixed(2)}
                </span>
              </div>
            )}

            {calculatedPrice.totalAmount > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="font-medium mb-3">Price Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Base Price:</span>
                    <span>${calculatedPrice.basePrice.toFixed(2)}</span>
                  </div>
                  {calculatedPrice.optionPrices > 0 && (
                    <div className="flex justify-between">
                      <span>Options:</span>
                      <span>${calculatedPrice.optionPrices.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>${calculatedPrice.subtotal.toFixed(2)}</span>
                  </div>
                  {calculatedPrice.gst > 0 && (
                    <div className="flex justify-between">
                      <span>GST (5%):</span>
                      <span>${calculatedPrice.gst.toFixed(2)}</span>
                    </div>
                  )}
                  {calculatedPrice.pst > 0 && (
                    <div className="flex justify-between">
                      <span>PST (7%):</span>
                      <span>${calculatedPrice.pst.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Summary Toggle Button */}
      {isMobile && (
        <button
          onClick={() => setShowMobileSummary(true)}
          className="fixed flex items-center gap-1 bottom-4 left-4 z-50 bg-white border-2 border-gray-300 rounded-full p-3 shadow-lg hover:shadow-xl transition-all"
          style={{
            borderColor: getConfigColor('primary', '#6366f1'),
            fontSize: '0.9rem',
            // backgroundColor: getConfigColorWithOpacity('primary', '10', '#6366f110')
          }}
        >
          <FileText className="w-4 h-4" />
          Booking Summary
        </button>
      )}

      {/* Mobile Summary Modal */}
      {showMobileSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-white w-full max-h-[80vh] rounded-t-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Booking Summary</h3>
              <button
                onClick={() => setShowMobileSummary(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {/* Mobile Summary Content - Same as desktop but mobile-optimized */}
              <div className="space-y-4">
                <div className={`summary-item ${service?.name ? "filled" : ""}`}>
                  <span className="summary-label">Service:</span>
                  <span className="summary-value">
                    {service?.name || "Select a service"}
                  </span>
                </div>

                <div className={`summary-item ${bookingType ? "filled" : ""}`}>
                  <span className="summary-label">Booking Type:</span>
                  <span className="summary-value">
                    {bookingType === "single" ? "Single Lesson" : bookingType === "package" ? "Package" : "Not selected"}
                  </span>
                </div>

                {selectedClass && (
                  <div className="summary-item filled">
                    <span className="summary-label">Class:</span>
                    <span className="summary-value">{selectedClass}</span>
                  </div>
                )}

                {bookingType === "package" && selectedPackage && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium mb-3">Selected Package:</h4>
                    <div className="text-sm bg-gray-50 p-3 rounded">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-semibold text-base">{selectedPackage.name}</div>
                        {selectedPackage.savings > 0 && (
                          <span className="text-xs font-bold px-2 py-1 rounded-full text-white"
                            style={{ backgroundColor: getConfigColor('secondary', '#f59e0b') }}>
                            Save ${selectedPackage.savings}
                          </span>
                        )}
                      </div>
                      <div className="text-gray-600 mb-1">{selectedPackage.lessons} lessons</div>
                      <div className="text-gray-600 mb-1">{selectedPackage.duration} min per lesson</div>
                      {selectedPackage.savings > 0 && (
                        <div className="text-gray-500 text-xs mb-1">
                          <span className="line-through">Regular: ${selectedPackage.price + selectedPackage.savings}</span>
                        </div>
                      )}
                      <div className="font-bold text-lg mt-2" style={{ color: getConfigColor('primary', '#6366f1') }}>
                        ${selectedPackage.price}
                      </div>
                    </div>
                  </div>
                )}

                {bookingType === "single" && selectedBookings.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium mb-3">Selected Bookings ({selectedBookings.length}):</h4>
                    <div className="space-y-2">
                      {selectedBookings.map((booking, index) => (
                        <div key={booking.id} className="text-sm bg-gray-50 p-2 rounded">
                          <div className="font-medium">
                            Booking {index + 1}:
                          </div>
                          <div>{formatSelectedDate(booking.date)}</div>
                          <div>{booking.startTime} - {booking.endTime}</div>
                          <div className="text-xs text-gray-600">
                            Duration: {booking.duration} minutes
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show extras if selected */}
                {(needsCarRental || needsRoadTestBooking) && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium mb-3">Additional Services:</h4>
                    <div className="space-y-2 text-sm">
                      {needsCarRental && (
                        <div className="flex justify-between">
                          <span>Car Rental for Road Test</span>
                          <span className="font-semibold">+$90</span>
                        </div>
                      )}
                      {needsRoadTestBooking && (
                        <div className="flex justify-between">
                          <span>Book My Road Test</span>
                          <span className="font-semibold">+$50</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {service?.requiresPayment && (
                  <div className="summary-item price">
                    <span className="summary-label">Total Price:</span>
                    <span className="summary-value">
                      ${calculatedPrice.totalAmount.toFixed(2)}
                    </span>
                  </div>
                )}

                {calculatedPrice.totalAmount > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium mb-3">Price Breakdown</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Base Price:</span>
                        <span>${calculatedPrice.basePrice.toFixed(2)}</span>
                      </div>
                      {calculatedPrice.optionPrices > 0 && (
                        <div className="flex justify-between">
                          <span>Options:</span>
                          <span>${calculatedPrice.optionPrices.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>${calculatedPrice.subtotal.toFixed(2)}</span>
                      </div>
                      {calculatedPrice.gst > 0 && (
                        <div className="flex justify-between">
                          <span>GST (5%):</span>
                          <span>${calculatedPrice.gst.toFixed(2)}</span>
                        </div>
                      )}
                      {calculatedPrice.pst > 0 && (
                        <div className="flex justify-between">
                          <span>PST (7%):</span>
                          <span>${calculatedPrice.pst.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-base border-t pt-2">
                        <span>Total:</span>
                        <span>${calculatedPrice.totalAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Progress Bar */
        .progress-container {
          position: relative;
          top: 0;
          left: 0;
          right: 0;
          background: white;
          padding: 1rem 5%;
          box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
        }

        .progress-bar {
          max-width: 800px;
          overflow-x: clip;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          min-width: 600px; /* Ensure minimum width for proper spacing */
        }

        .progress-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          position: relative;
          z-index: 2;
        }

        .step-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #d1d5db;
          border: 3px solid #d1d5db;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          font-weight: 600;
          transition: all 0.5s ease;
        }

        .step-circle.active {
          background: var(--primary);
          border-color: var(--primary);
          color: white;
          transform: scale(1.1);
        }

        .step-circle.completed {
          background: #10b981;
          border-color: #10b981;
          color: white;
        }

        .step-label {
          font-size: 0.875rem;
          font-weight: 500;
          margin-top: 20px;
          color: #6b7280;
          text-align: center;
          transition: color 0.3s ease;
        }

        .step-label.active {
          color: var(--primary);
          font-weight: 600;
        }

        .progress-line {
          position: absolute;
          top: 20px;
          left: 0;
          right: 0;
          height: 3px;
          background: #d1d5db;
          z-index: 1;
        }

        .progress-line-fill {
          height: 100%;
          background: var(--primary);
          transition: width 0.8s ease;
        }

        /* Hero Section */
        .booking-hero {
          padding: 8rem 5% 4rem;
          margin-top: 80px;
          background: var(
            --gradient-1,
            linear-gradient(135deg, #667eea 0%, #764ba2 100%)
          );
          background-size: cover;
          background-position: center;
          color: white;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .booking-hero::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          z-index: 0;
        }

        .booking-hero-content {
          position: relative;
          z-index: 1;
          max-width: 800px;
          margin: 0 auto;
        }

        .booking-hero h1 {
          font-size: clamp(1rem, 2vw, 2rem);
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .booking-hero p {
          font-size: 1.2rem;
          opacity: 0.9;
          margin-bottom: 2rem;
        }

        /* Main Content */
        .booking-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 4rem 5% 6rem;
          margin-top: 50px;
          overflow-x: hidden;
        }

        .booking-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 3rem;
          align-items: start;
        }

        /* Booking Form */
        .booking-form {
          background: white;
          border-radius: 20px;
          box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1),
            0 8px 10px -6px rgb(0 0 0 / 0.1);
          overflow: hidden;
        }

        .form-section {
          padding: 2rem;
          padding-top: 2.5rem;
          overflow: visible;
          border-bottom: 1px solid #e5e7eb;
          opacity: 1;
          transform: translateY(0);
          transition: all 0.5s ease;
        }

        .form-section:last-child {
          border-bottom: none;
        }

        .form-section.active {
          animation: slideInUp 0.6s ease;
        }

        .section-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--dark);
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .section-icon {
          width: 40px;
          height: 40px;
          background: var(--primary);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.1rem;
          transition: background 0.3s ease;
        }

        /* Time Slots */
        .time-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .time-card {
          border: 2px solid #d1d5db;
          border-radius: 12px;
          
          text-align: center;
          cursor: pointer;
          transition: all 0.4s ease;
          background: white;
          opacity: 1;
          transform: scale(1);
        }

        .time-card:hover {
          border-color: var(--primary);
          transform: translateY(-3px) scale(1);
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1),
            0 4px 6px -4px rgb(0 0 0 / 0.1);
        }

        .time-card.selected {
          border-color: var(--primary);
          background: rgba(99, 102, 241, 0.05);
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1),
            0 4px 6px -4px rgb(0 0 0 / 0.1);
        }

        /* Form Elements */
        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
                    font-size: 0.9rem;

        }

        .form-input {
          width: 100%;
          padding: 0.875rem 1rem;
          border: 2px solid #d1d5db;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: white;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
          transform: translateY(-1px);
        }

        /* Submit Button */
        .submit-btn {
          background: var(--primary);
          color: white;
          padding: 1rem;
          border: none;
          border-radius: 12px;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.4s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          min-width: 200px;
          max-width: 100%;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1),
            0 8px 10px -6px rgb(0 0 0 / 0.1);
        }

        .submit-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        /* Summary Card */
        .booking-summary {
          background: white;
          border-radius: 20px;
          box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1),
            0 8px 10px -6px rgb(0 0 0 / 0.1);
          padding: 2rem;
          position: sticky;
          top: 120px;
          animation: slideInRight 0.8s ease;
        }

        .summary-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: var(--dark);
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid #e5e7eb;
          opacity: 0.5;
          transition: opacity 0.5s ease;
           }

        .summary-item.filled {
          opacity: 1;
        }

        .summary-item:last-child {
          border-bottom: none;
          font-weight: 700;
          font-size: 1.1rem;
          color: var(--primary);
        }

        .summary-label {
          color: var(--text-secondary);
        }

        .summary-value {
          font-weight: 600;
          color: var(--dark);
        }

        /* Animations */
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        /* Mobile Responsive */
        @media (max-width: 1024px) {
          .booking-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .booking-summary {
            position: static;
            order: -1;
          }

          .progress-bar {
            flex-wrap: wrap;
            gap: 1rem;
            min-width: auto;
            justify-content: center;
          }

          .step-label {
            font-size: 0.75rem;
          }
        }

        @media (max-width: 768px) {
          .booking-hero {
            padding: 6rem 5% 3rem;
          }

          .booking-container {
            padding: 2rem 5% 4rem;
          }

          .time-grid {
            grid-template-columns: repeat(3, 1fr);
          }

          .form-section {
            padding: 1.5rem;
          }

          .booking-summary {
            padding: 1.5rem;
          }

          .progress-container {
            padding: 0.5rem 5%;
          }

          .step-circle {
            width: 35px;
            height: 35px;
          }

          .submit-btn {
            min-width: 150px;
            padding: 0.75rem;
            font-size: 0.9rem;
          }
        }

        @media (max-width: 480px) {
          .section-title {
            font-size: 1.25rem;
          }

          .time-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .progress-bar {
            justify-content: center;
            min-width: auto;
            flex-wrap: wrap;
            gap: 0.5rem;
          }

          .step-label {
            display: none;
          }

          .progress-container {
            padding: 0.5rem 2%;
            overflow-x: auto;
          }

          .submit-btn {
            min-width: 120px;
            padding: 0.5rem;
            font-size: 0.85rem;
          }
        }

        /* Scroll target animation */
        .scroll-target {
          transition: all 0.3s ease;
        }

        .scroll-target:target,
        .scroll-target.highlight {
          animation: highlightSection 2s ease-in-out;
        }

        @keyframes highlightSection {
          0% {
            background-color: transparent;
            transform: scale(1);
          }
          15% {
            background-color: rgba(99, 102, 241, 0.05);
            transform: scale(1.01);
          }
          85% {
            background-color: rgba(99, 102, 241, 0.05);
            transform: scale(1.01);
          }
          100% {
            background-color: transparent;
            transform: scale(1);
          }
        }
      `}</style>

      {/* Call Assistance Modal */}
      {showCallAssistanceModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={proceedToNextStepAfterModal}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b flex justify-between items-center" style={{ backgroundColor: getConfigColor('primary', '#6366f1') }}>
              <h3 className="text-white font-semibold text-xl">Need Help Booking?</h3>
              <button
                onClick={closeCallAssistanceModal}
                className="text-white border-none outline-none  shadow-none hover:shadow-none transition-none hover:scale-105 text-sm"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-6 text-center">
                Would you like us to help you complete your booking over the phone, or would you prefer to continue yourself?
              </p>

              <div className="space-y-3 mb-4">
                {/* Phone Numbers */}
                <div className="border-2 rounded-lg p-4" style={{ borderColor: getConfigColor('primary', '#6366f1') }}>
                  <p className="text-sm text-gray-600 mb-3 font-medium">Call us to complete your booking:</p>
                  <a
                    href="tel:7787585859"
                    className="block font-semibold hover:underline mb-2 flex items-center gap-2"
                    style={{ color: getConfigColor('primary', '#6366f1') }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    (778) 758-5859
                  </a>
                  <a
                    href="tel:8668666630"
                    className="block font-semibold hover:underline mb-3 flex items-center gap-2"
                    style={{ color: getConfigColor('primary', '#6366f1') }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    (866) 866-6630
                  </a>
                  <a
                    href="https://wa.me/17787585859"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block font-semibold hover:underline flex items-center gap-2"
                    style={{ color: '#25D366' }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="#25D366"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    WhatsApp Call
                  </a>
                </div>
              </div>

              <button
                onClick={proceedToNextStepAfterModal}
                className="w-full text-white py-3 px-4 rounded-lg hover:opacity-90 transition-all font-semibold"
                style={{ backgroundColor: getConfigColor('primary', '#6366f1') }}
              >
                Continue Booking Myself
              </button>


            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const BookingPage = () => {
  const { slug } = useParams();
  const { config } = useConfig(); // Add this line to get config
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stripeReady, setStripeReady] = useState(false);

  // Add helper functions to BookingPage component
  const getConfigColor = (colorKey, fallback) => {
    return config?.colors?.[colorKey] || fallback;
  };

  const getConfigColorWithOpacity = (colorKey, opacity, fallback) => {
    const color = getConfigColor(colorKey, fallback);
    return `${color}${opacity}`;
  };

  useEffect(() => {
    if (slug) {
      if (!document.querySelector('script[src*="AW-17986540646"]')) {
        const script = document.createElement("script");
        script.async = true;
        script.src =
          "https://www.googletagmanager.com/gtag/js?id=AW-17986540646";
        document.head.appendChild(script);
      }
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", "AW-17986540646");
    }
  }, [slug]);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await axios.get(`/api/booking-services/${slug}`);
        setService(response.data);
      } catch (error) {
        console.error("Error fetching service:", error);
        setError("Service not found");
      } finally {
        setLoading(false);
      }
    };

    const initializeStripe = async () => {
      try {
        await getStripePromise();
        setStripeReady(true);
      } catch (error) {
        console.error("Error initializing Stripe:", error);
        setStripeReady(true); // Continue anyway for non-payment services
      }
    };

    if (slug) {
      fetchService();
      initializeStripe();
    }
  }, [slug]);

  if (loading || !stripeReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2
            className="w-8 h-8 animate-spin mx-auto mb-4"
            style={{ color: getConfigColor('primary', '#6366f1') }}
          />
          <p className="text-gray-600">Loading booking service...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Service Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            The booking service you're looking for doesn't exist.
          </p>
          <button
            onClick={() => window.history.back()}
            className="text-white px-6 py-2 rounded-lg"
            style={{ backgroundColor: getConfigColor('primary', '#6366f1') }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!service.isActive) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Service Unavailable
          </h1>
          <p className="text-gray-600 mb-4">
            This booking service is currently unavailable.
          </p>
          <button
            onClick={() => window.history.back()}
            className="text-white px-6 py-2 rounded-lg"
            style={{ backgroundColor: getConfigColor('primary', '#6366f1') }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <BookingForm service={service} />
    </Elements>
  );
};

export default BookingPage;
